/* global subscribe, PUB_SUB_EVENTS */

(() => {
  "use strict";

  const selectors = {
    section: '[data-pf-experience="pf_scroll_pilot"]',
    row: ".pf-product-scroll__row",
    mediaPanel: ".pf-product-scroll__media-panel",
    mediaItem: ".pf-product-scroll__media-item",
    contentBox: ".pf-product-scroll__content-box",
    variantOption: ".pf-product-scroll__variant-option",
    complementaryOption: "[data-pf-scroll-complementary-option]",
    warrantySelector: "[data-pf-scroll-warranty-selector]",
  };

  // Pilot templates the page must stay on through in-place product swaps: the default pilot view and
  // the old Shoplift protection test variant (templates/product.protection-quick.json), the
  // protection-first order test (templates/product.protection-first.json), the guided option
  // steps test (templates/product.guided-options.json) and the trade-in test
  // (templates/product.trade-in.json).
  const PF_SCROLL_PILOT_VIEWS = [
    "pf-scroll-pilot",
    "protection-quick",
    "protection-first",
    "guided-options",
    "trade-in",
  ];
  const initialPfScrollUrl = new URL(window.location.href);
  const pfScrollPilotView = PF_SCROLL_PILOT_VIEWS.includes(
    initialPfScrollUrl.searchParams.get("view")
  )
    ? initialPfScrollUrl.searchParams.get("view")
    : "";
  const pfScrollPilotViewRequested = Boolean(pfScrollPilotView);
  const pfScrollPilotPreviewThemeId = initialPfScrollUrl.searchParams.get("preview_theme_id") || "";

  const debugPfScrollVariantSelectors = () => {};

  const debugPfScrollAddOns = () => {};

  const isPfScrollDesignMode = () => Boolean(window.Shopify?.designMode);

  const isLivePfScrollSection = (section) => {
    if (!section?.isConnected) return false;
    if (isPfScrollDesignMode()) return true;

    const main = section.closest("main");
    return main?.style?.display !== "none";
  };

  const getPfScrollSectionsForInit = (root = document) =>
    // eslint-disable-next-line
    getPfScrollSections(root).filter(isLivePfScrollSection);

  const isPfProductScrollSectionRoot = (root) =>
    Boolean(root?.matches?.(selectors.section) || root?.querySelector?.(selectors.section));

  const getPfScrollSections = (root = document) => {
    if (!root?.querySelectorAll) return [];

    const sections = [];
    if (root.matches?.(selectors.section)) {
      sections.push(root);
    }
    sections.push(...root.querySelectorAll(selectors.section));

    return Array.from(new Set(sections));
  };

  const getLivePfScrollSections = (root = document) =>
    getPfScrollSections(root).filter(isLivePfScrollSection);

  const getPfScrollDebugSectionId = (section) =>
    section?.closest?.('[id^="shopify-section-"]')?.id || section?.id || "";

  const getPfScrollPreviewThemeId = () => {
    const queryThemeId = new URL(window.location.href).searchParams.get("preview_theme_id");
    if (queryThemeId) return queryThemeId;
    const currentTheme = window.Shopify?.theme;
    return currentTheme?.role === "unpublished" && currentTheme.id ? String(currentTheme.id) : "";
  };

  const restorePfScrollPilotViewQuery = () => {
    if (!pfScrollPilotViewRequested) return;
    const currentUrl = new URL(window.location.href);
    const viewIsCurrent = currentUrl.searchParams.get("view") === pfScrollPilotView;
    const previewIsCurrent =
      !pfScrollPilotPreviewThemeId ||
      currentUrl.searchParams.get("preview_theme_id") === pfScrollPilotPreviewThemeId;
    if (viewIsCurrent && previewIsCurrent) return;

    currentUrl.searchParams.set("view", pfScrollPilotView);
    if (pfScrollPilotPreviewThemeId) {
      currentUrl.searchParams.set("preview_theme_id", pfScrollPilotPreviewThemeId);
    }
    window.history.replaceState(
      window.history.state,
      "",
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
    );
  };

  const installPfScrollPilotHistoryGuard = () => {
    if (!pfScrollPilotViewRequested || window.pfScrollPilotHistoryGuardInstalled) return;
    const replaceState = window.history.replaceState.bind(window.history);

    window.history.replaceState = (state, unused, url) => {
      if (url === undefined || url === null) {
        return replaceState(state, unused, url);
      }

      const destination = new URL(String(url), window.location.href);
      if (destination.origin !== window.location.origin) {
        return replaceState(state, unused, url);
      }

      destination.searchParams.set("view", pfScrollPilotView);
      if (pfScrollPilotPreviewThemeId) {
        destination.searchParams.set("preview_theme_id", pfScrollPilotPreviewThemeId);
      }
      return replaceState(
        state,
        unused,
        `${destination.pathname}${destination.search}${destination.hash}`
      );
    };
    window.pfScrollPilotHistoryGuardInstalled = true;
  };

  installPfScrollPilotHistoryGuard();

  let pfCtaDrawerEscapeBound = false;
  const pfCtaDrawerOpenerSelector = "[data-pf-cta-open-video], [data-pf-cta-open-drawer]";
  const pfCtaDrawerFocusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';
  const pfCtaDrawerReturnFocus = new WeakMap();

  const getPfCtaDrawerFocusables = (drawer) =>
    Array.from(drawer.querySelectorAll(pfCtaDrawerFocusableSelector)).filter(
      (el) => !el.closest("[hidden]") && el.getClientRects().length > 0
    );

  // Guides seen or opened on this page, reported with any add-to-cart that follows.
  const pfChoiceHelpersExposedOnPage = new Set();
  const pfChoiceHelpersOpenedOnPage = new Set();

  const emitPfChoiceHelperEvent = (eventName, helperRoot) => {
    const helper = helperRoot?.getAttribute?.("data-pf-choice-helper");
    if (!helper) return;
    if (eventName === "pdp_helper_exposed") pfChoiceHelpersExposedOnPage.add(helper);
    if (eventName === "pdp_helper_opened") pfChoiceHelpersOpenedOnPage.add(helper);
    const section = helperRoot.closest(selectors.section);
    const detail = {
      helper,
      product_id: String(getPfScrollProductId(section) || ""),
      product_handle: window.location.pathname.split("/products/")[1]?.split("/")[0] || "",
      source: "pf_scroll",
      pdp_experience: "pf_scroll_pilot",
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...detail });
    window.posthog?.capture?.(eventName, detail);
  };

  const pfChoiceHelperExposed = new WeakSet();
  // A helper counts as seen only after it stays half visible for this long, so the
  // brief overlap while the mobile layout settles on load is not reported.
  const pfChoiceHelperDwellMs = 1000;
  const pfChoiceHelperDwellTimers = new WeakMap();
  const pfChoiceHelperPendingRoots = new Set();
  let pfChoiceHelperShopperActive = false;
  let pfChoiceHelperStartBound = false;
  let pfChoiceHelperObserver = null;
  const observePfChoiceHelpers = (root) => {
    if (!root?.querySelectorAll) return;
    // Start timing once the layout has settled: the page has loaded or the shopper
    // has started interacting with it, whichever comes first.
    if (document.readyState !== "complete" && !pfChoiceHelperShopperActive) {
      pfChoiceHelperPendingRoots.add(root);
      if (!pfChoiceHelperStartBound) {
        pfChoiceHelperStartBound = true;
        // Only the window's own load event counts; image loads must not start timing.
        const inputEvents = ["pointerdown", "touchstart", "wheel", "keydown"];
        const start = () => {
          if (pfChoiceHelperShopperActive) return;
          pfChoiceHelperShopperActive = true;
          window.removeEventListener("load", start);
          inputEvents.forEach((type) => window.removeEventListener(type, start, true));
          pfChoiceHelperPendingRoots.forEach((pendingRoot) => observePfChoiceHelpers(pendingRoot));
          pfChoiceHelperPendingRoots.clear();
        };
        window.addEventListener("load", start);
        inputEvents.forEach((type) =>
          window.addEventListener(type, start, { capture: true, passive: true })
        );
      }
      return;
    }
    const helpers = Array.from(root.querySelectorAll("[data-pf-choice-helper]")).filter(
      (helper) => !pfChoiceHelperExposed.has(helper)
    );
    if (!helpers.length) return;
    if (!("IntersectionObserver" in window)) return;
    if (!pfChoiceHelperObserver) {
      pfChoiceHelperObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const helper = entry.target;
            if (pfChoiceHelperExposed.has(helper)) return;
            if (!entry.isIntersecting) {
              clearTimeout(pfChoiceHelperDwellTimers.get(helper));
              pfChoiceHelperDwellTimers.delete(helper);
              return;
            }
            if (pfChoiceHelperDwellTimers.has(helper)) return;
            pfChoiceHelperDwellTimers.set(
              helper,
              setTimeout(() => {
                pfChoiceHelperDwellTimers.delete(helper);
                if (!helper.isConnected || pfChoiceHelperExposed.has(helper)) return;
                pfChoiceHelperExposed.add(helper);
                pfChoiceHelperObserver.unobserve(helper);
                emitPfChoiceHelperEvent("pdp_helper_exposed", helper);
              }, pfChoiceHelperDwellMs)
            );
          });
        },
        { threshold: 0.5 }
      );
    }
    helpers.forEach((helper) => pfChoiceHelperObserver.observe(helper));
  };

  function syncPfCtaDrawerBodyOverflow() {
    const hasOpen = document.querySelector("[data-pf-cta-drawer].active");
    document.body.classList.toggle("overflow-hidden", Boolean(hasOpen));
  }

  function startPfCtaDrawerVideoPlayback(drawer) {
    const stage = drawer.querySelector("[data-pf-cta-drawer-video-stage]");
    const video = stage?.querySelector("video");
    if (!stage || !video || stage.classList.contains("is-playing")) return;
    video.controls = true;
    stage.classList.add("is-playing");
    const playResult = video.play();
    if (playResult !== undefined && typeof playResult.catch === "function") {
      playResult.catch(() => {
        stage.classList.remove("is-playing");
        video.controls = false;
      });
    }
  }

  function resetPfCtaDrawerVideo(drawer) {
    if (!drawer) return;
    const stage = drawer.querySelector("[data-pf-cta-drawer-video-stage]");
    const video = stage?.querySelector("video") || drawer.querySelector("video");
    if (video) {
      video.pause();
      video.controls = false;
      try {
        video.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
    stage?.classList.remove("is-playing");
  }

  function closePfCtaDrawer(drawer) {
    if (!drawer) return;
    resetPfCtaDrawerVideo(drawer);
    drawer.classList.remove("active");
    const ctaRoot = drawer.closest(".pf-product-scroll__cta-block-root");
    ctaRoot?.querySelectorAll(pfCtaDrawerOpenerSelector).forEach((opener) => {
      opener.setAttribute("aria-expanded", "false");
    });
    syncPfCtaDrawerBodyOverflow();
    const returnFocusTo = pfCtaDrawerReturnFocus.get(drawer);
    pfCtaDrawerReturnFocus.delete(drawer);
    if (returnFocusTo?.isConnected) returnFocusTo.focus({ preventScroll: true });
    const durationMs = 280;
    window.setTimeout(() => {
      if (!drawer.classList.contains("active")) {
        drawer.setAttribute("hidden", "");
      }
    }, durationMs);
  }

  function openPfCtaDrawer(drawer, opener) {
    if (!drawer) return;
    resetPfCtaDrawerVideo(drawer);
    if (opener) pfCtaDrawerReturnFocus.set(drawer, opener);
    drawer.removeAttribute("hidden");
    window.requestAnimationFrame(() => {
      drawer.classList.add("active");
      const ctaRoot = drawer.closest(".pf-product-scroll__cta-block-root");
      ctaRoot?.querySelectorAll(pfCtaDrawerOpenerSelector).forEach((el) => {
        el.setAttribute("aria-expanded", "true");
      });
      syncPfCtaDrawerBodyOverflow();
      const initialFocus =
        drawer.querySelector(".pf-product-scroll__cta-video-drawer__close") ||
        getPfCtaDrawerFocusables(drawer)[0] ||
        drawer;
      initialFocus.focus({ preventScroll: true });
    });
  }

  function trapPfCtaDrawerFocus(event, drawer) {
    const focusables = getPfCtaDrawerFocusables(drawer);
    if (!focusables.length) {
      event.preventDefault();
      drawer.focus({ preventScroll: true });
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !drawer.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !drawer.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindPfCtaDrawerEscapeOnce() {
    if (pfCtaDrawerEscapeBound) return;
    pfCtaDrawerEscapeBound = true;
    document.addEventListener("keydown", (event) => {
      if (event.key === "Tab") {
        const openDrawer = document.querySelector("[data-pf-cta-drawer].active");
        if (openDrawer) trapPfCtaDrawerFocus(event, openDrawer);
        return;
      }
      if (event.code !== "Escape") return;
      document.querySelectorAll("[data-pf-cta-drawer].active").forEach((drawer) => {
        closePfCtaDrawer(drawer);
      });
    });
  }

  function initPfCtaVideoDrawers(root) {
    if (!root?.querySelectorAll) return;
    bindPfCtaDrawerEscapeOnce();
    observePfChoiceHelpers(root);
    root.querySelectorAll("[data-pf-cta-drawer]").forEach((drawer) => {
      if (drawer.dataset.pfCtaDrawerInit === "true") return;
      const ctaRoot = drawer.closest(".pf-product-scroll__cta-block-root");
      const openers = ctaRoot
        ? Array.from(ctaRoot.querySelectorAll(pfCtaDrawerOpenerSelector))
        : [];
      if (!ctaRoot || !openers.length) return;
      drawer.dataset.pfCtaDrawerInit = "true";
      const helperRoot = ctaRoot.closest("[data-pf-choice-helper]");

      openers.forEach((opener) => {
        opener.addEventListener("click", (event) => {
          event.preventDefault();
          openPfCtaDrawer(drawer, opener);
          if (helperRoot) emitPfChoiceHelperEvent("pdp_helper_opened", helperRoot);
        });
      });

      drawer.querySelectorAll("[data-pf-cta-drawer-close]").forEach((el) => {
        el.addEventListener("click", () => closePfCtaDrawer(drawer));
      });

      const stage = drawer.querySelector("[data-pf-cta-drawer-video-stage]");
      stage?.addEventListener("click", (event) => {
        if (stage.classList.contains("is-playing")) return;
        const { target } = event;
        if (target.closest("[data-pf-cta-drawer-close]")) return;
        event.preventDefault();
        startPfCtaDrawerVideoPlayback(drawer);
      });
    });
  }

  const getMainProductInfo = () =>
    document
      .querySelector("product-info:not([data-pf-scroll-product-info]) .product__info-container")
      ?.closest?.("product-info");

  const getPfScrollProductInfo = (section) =>
    section?.querySelector("[data-pf-scroll-product-info]") || null;

  const PF_SCROLL_COMPLEMENTARY_INPUT_NAME = "pf_scroll_complementary_variant_id[]";

  const getProductInfoVariantSelectSources = (section = null) => {
    const mainProductInfo = getMainProductInfo();
    const mainVariantSelects = mainProductInfo?.querySelector(
      '.product__info-container variant-selects:not([data-upsell-isolated="true"])'
    );
    const scrollVariantSelects = getPfScrollProductInfo(section)?.querySelector(
      'variant-selects:not([data-upsell-isolated="true"])'
    );
    const sources = Array.from(new Set([mainVariantSelects, scrollVariantSelects].filter(Boolean)));
    debugPfScrollVariantSelectors("source lookup", {
      sectionId: section?.id,
      mainProductInfoId: mainProductInfo?.id,
      hasMainVariantSelects: Boolean(mainVariantSelects),
      hasScrollVariantSelects: Boolean(scrollVariantSelects),
      sourceIds: sources.map((source) => source.id),
    });
    return sources;
  };

  const getProductInfoVariantSelects = (section = null) =>
    getProductInfoVariantSelectSources(section)[0] || null;

  const normalizePfScrollOptionName = (value) =>
    String(value || "")
      .replace(/^\s*select\s+/i, "")
      .replace(/:\s*$/, "")
      .trim()
      .toLowerCase();

  const isPfScrollConditionOptionName = (value) =>
    normalizePfScrollOptionName(value).includes("condition");

  const getPfScrollFieldsetOptionName = (fieldset) => {
    if (!fieldset) return "";
    const fromDataset = fieldset.dataset.optionName || fieldset.dataset.option;
    if (fromDataset) return String(fromDataset).trim();
    const label = fieldset.querySelector("legend.form__label, label.form__label");
    const labelText = label?.textContent?.split(":")[0] || "";
    return labelText.replace(/^\s*select\s+/i, "").trim();
  };

  const getPfScrollOptionRows = (section) =>
    Array.from(section?.querySelectorAll?.("[data-pf-option-row]") || []);

  const getPfScrollConditionOptionPosition = (section) => {
    const adaptiveRoot = section?.querySelector?.("[data-pf-adaptive-options]");
    const declared = Number(adaptiveRoot?.dataset?.pfConditionOptionPosition);
    if (declared > 0) return declared;

    const row = getPfScrollOptionRows(section).find((candidate) =>
      isPfScrollConditionOptionName(candidate.dataset.optionName)
    );
    if (row) return Number(row.dataset.optionPosition) || 0;

    const source = getProductInfoVariantSelects(section);
    const fieldsets = Array.from(
      source?.querySelectorAll?.("fieldset.js.product-form__input") || []
    );
    const sourceIndex = fieldsets.findIndex((fieldset) =>
      isPfScrollConditionOptionName(getPfScrollFieldsetOptionName(fieldset))
    );
    return sourceIndex >= 0 ? sourceIndex + 1 : 0;
  };

  const findFieldsetByOptionIndex = (variantSelectsRoot, optionIndex) => {
    if (!variantSelectsRoot || !optionIndex) return null;
    const byAttr = variantSelectsRoot.querySelector(`fieldset[data-option-index="${optionIndex}"]`);
    if (byAttr) return byAttr;
    const ordered = Array.from(
      variantSelectsRoot.querySelectorAll("fieldset.js.product-form__input")
    );
    return ordered[optionIndex - 1] || null;
  };

  const getPfScrollOptionNameByPosition = (section, position) => {
    const row = getPfScrollOptionRows(section).find(
      (candidate) => Number(candidate.dataset.optionPosition) === Number(position)
    );
    if (row?.dataset.optionName) return row.dataset.optionName;

    const fieldset = findFieldsetByOptionIndex(getProductInfoVariantSelects(section), position);
    return getPfScrollFieldsetOptionName(fieldset);
  };

  const findFieldsetForPfScrollTarget = (variantSelectsRoot, target, optionIndex) => {
    if (!variantSelectsRoot) return null;
    const targetName = normalizePfScrollOptionName(target?.dataset?.optionName);
    if (targetName) {
      const namedMatch = Array.from(
        variantSelectsRoot.querySelectorAll("fieldset.js.product-form__input")
      ).find(
        (fieldset) =>
          normalizePfScrollOptionName(getPfScrollFieldsetOptionName(fieldset)) === targetName
      );
      if (namedMatch) return namedMatch;
    }
    return findFieldsetByOptionIndex(variantSelectsRoot, optionIndex);
  };

  const findPfScrollVariantSourceForTarget = (section, target, optionIndex) => {
    const sources = getProductInfoVariantSelectSources(section);
    return (
      sources
        .map((source) => ({
          fieldset: findFieldsetForPfScrollTarget(source, target, optionIndex),
          source,
        }))
        .find(({ fieldset }) => Boolean(fieldset)) || {
        fieldset: null,
        source: sources[0] || null,
      }
    );
  };

  /** PDP keeps "Select …"; scroll clones strip the English prefix from live markup. */
  const stripSelectPrefixFromPfScrollVariantClone = (root) => {
    if (!root?.querySelectorAll) return;
    const prefix = /^\s*Select /;
    root.querySelectorAll("legend.form__label, label.form__label").forEach((el) => {
      const first = el.firstChild;
      if (first && first.nodeType === Node.TEXT_NODE && prefix.test(first.textContent)) {
        first.textContent = first.textContent.replace(prefix, "");
        return;
      }
      const sw = el.querySelector(".selected-swatch-value");
      if (!sw && prefix.test(el.textContent)) {
        el.textContent = el.textContent.replace(prefix, "");
      }
    });
  };

  function syncPfScrollGalleryMainModals() {
    const modal = document.querySelector('product-modal[id^="ProductModal-"]');
    if (!modal?.id) return;
    document.querySelectorAll("modal-opener[data-pf-sync-main-modal]").forEach((opener) => {
      opener.setAttribute("data-modal", `#${modal.id}`);
    });
  }

  const stripPfThemeGalleryBindMarkers = (root) => {
    if (!root?.querySelectorAll) return;
    root.querySelectorAll("[data-pf-theme-segments-bound]").forEach((el) => {
      el.removeAttribute("data-pf-theme-segments-bound");
    });
  };

  /** Session-stashed review slot outerHTML may predate removal of this element from Liquid. */
  const stripLegacyPfScrollReviewMarkup = (root) => {
    if (!root?.querySelectorAll) return;
    root.querySelectorAll(".pf-scroll-review-card__label-note").forEach((el) => el.remove());
  };

  function initPfThemeGallerySegmentBars(root) {
    if (!root?.querySelectorAll) return;
    root
      .querySelectorAll('.pf-product-gallery--theme slider-component[id^="GalleryViewer-"]')
      .forEach((viewer) => {
        if (viewer.dataset.pfThemeSegmentsBound === "true") return;
        const segmentsWrap = viewer.querySelector("[data-pf-theme-segments]");
        if (!segmentsWrap) return;

        const syncSegmentsToSlide = (slideEl) => {
          const slides = Array.from(viewer.querySelectorAll(".product__media-item.slider__slide"));
          const idx = slideEl ? slides.indexOf(slideEl) : -1;
          const resolvedIdx = idx >= 0 ? idx : 0;
          const segments = segmentsWrap.querySelectorAll(".pf-theme-gallery__segment");
          segments.forEach((seg, i) => {
            seg.classList.toggle("is-active", i === resolvedIdx);
          });
        };

        const onSlideChanged = (event) => {
          const { currentElement, currentPage } = event.detail || {};
          if (currentElement) {
            syncSegmentsToSlide(currentElement);
            return;
          }
          if (currentPage != null) {
            const slides = viewer.querySelectorAll(".product__media-item.slider__slide");
            const page = Number(currentPage);
            const clamped = Math.max(1, Math.min(slides.length, Number.isFinite(page) ? page : 1));
            syncSegmentsToSlide(slides[clamped - 1]);
          }
        };

        viewer.addEventListener("slideChanged", onSlideChanged);
        viewer.dataset.pfThemeSegmentsBound = "true";

        const activeSlide =
          viewer.querySelector(".product__media-item.slider__slide.is-active") ||
          viewer.querySelector(".product__media-item.slider__slide");
        syncSegmentsToSlide(activeSlide);
      });
  }

  const REDIRECT_SCROLL_STASH_KEY = "pf-pilot-product-scroll-redirect-focus";
  const POST_AJAX_VARIANT_SCROLL_STASH_KEY = "pf-pilot-product-scroll-post-ajax-variant-scroll";
  const HTML_HIDE_MAIN_VARIANTS_CLASS = "pf-pilot-scroll-hide-main-variant-selects";

  const applyMainVariantVisibilityFromDom = () => {
    const shouldHide = Boolean(
      document.querySelector(`${selectors.section} [data-hide-main-variant-selects="true"]`)
    );
    document.documentElement.classList.toggle(HTML_HIDE_MAIN_VARIANTS_CLASS, shouldHide);
  };

  const stashRedirectScrollTarget = (
    section,
    variantOptionTarget,
    productInfo = getMainProductInfo()
  ) => {
    if (productInfo?.dataset.redirectOnVariantChange !== "true") return;

    const shopifySectionEl = section.closest('[id^="shopify-section-"]');
    const variantOptionBlock = variantOptionTarget?.dataset?.variantOptionBlock || "";

    sessionStorage.setItem(
      REDIRECT_SCROLL_STASH_KEY,
      JSON.stringify({
        sectionId: shopifySectionEl?.id || "",
        variantOptionBlock,
      })
    );
  };

  const escapeVariantOptionBlockSelector = (value) =>
    String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

  const escapeHTML = (value) => {
    const node = document.createElement("span");
    node.textContent = value ?? "";
    return node.innerHTML;
  };

  const scrollPfVariantOptionIntoView = (sectionId, variantOptionBlock) => {
    if (!sectionId) return;
    const root = document.getElementById(sectionId);
    if (!root) return;

    let focusEl = null;
    if (variantOptionBlock) {
      focusEl = root.querySelector(
        `${selectors.variantOption}[data-variant-option-block="${escapeVariantOptionBlockSelector(
          variantOptionBlock
        )}"]`
      );
    }
    if (!focusEl) {
      focusEl = root.querySelector(selectors.variantOption);
    }

    const el = focusEl || root;
    el.scrollIntoView({ block: "center", behavior: "auto" });
  };

  const restoreRedirectScrollTarget = () => {
    const raw = sessionStorage.getItem(REDIRECT_SCROLL_STASH_KEY);
    if (!raw) return;
    sessionStorage.removeItem(REDIRECT_SCROLL_STASH_KEY);

    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }

    const { sectionId, variantOptionBlock } = payload;
    const run = () => scrollPfVariantOptionIntoView(sectionId, variantOptionBlock);
    window.requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 120);
    });
  };

  const variantSectionRefreshers = new WeakMap();
  /** Last variant from PUB_SUB variantChange (swap path may run before [data-selected-variant] JSON updates). */
  let pfScrollLastPublishedVariant = null;

  const getPfScrollSelectedVariant = (productInfo = null) => {
    if (productInfo?.matches?.("[data-pf-scroll-product-info]") && pfScrollLastPublishedVariant) {
      return pfScrollLastPublishedVariant;
    }
    const sourceProductInfo =
      productInfo ||
      document.querySelector("[data-pf-scroll-product-info]") ||
      getMainProductInfo();
    const script = sourceProductInfo?.querySelector(
      'variant-selects:not([data-upsell-isolated="true"]) [data-selected-variant]'
    );
    if (script?.textContent?.trim()) {
      try {
        return JSON.parse(script.textContent.trim());
      } catch {
        // fall through to last published variant
      }
    }
    return pfScrollLastPublishedVariant;
  };

  const isPfScrollDefaultTitle = (value) => normalizePfScrollOptionName(value) === "default title";

  const sanitizePfScrollSelectedVariantSummaries = (section) => {
    section?.querySelectorAll?.("[data-pf-scroll-selected-variant-summary]").forEach((summary) => {
      const value = summary.querySelector("[data-pf-scroll-selected-variant-value]");
      if (!value) return;
      value.querySelectorAll(".pf-scroll-selected-variant__chip").forEach((chip) => {
        if (isPfScrollDefaultTitle(chip.textContent)) chip.remove();
      });
      summary.hidden = value.children.length === 0;
    });
  };

  const updatePfScrollSelectedVariantSummaries = (section, variant) => {
    if (!section || !variant?.options?.length) {
      sanitizePfScrollSelectedVariantSummaries(section);
      return;
    }
    const conditionPosition = getPfScrollConditionOptionPosition(section);
    const optionEntries = variant.options
      .map((option, index) => ({
        name: getPfScrollOptionNameByPosition(section, index + 1),
        position: index + 1,
        value: option,
      }))
      .filter(({ value }) => !isPfScrollDefaultTitle(value));

    section.querySelectorAll("[data-pf-scroll-selected-variant-summary]").forEach((summary) => {
      const value = summary.querySelector("[data-pf-scroll-selected-variant-value]");
      if (!value) return;
      value.innerHTML = optionEntries
        .map(({ name, position, value: optionValue }) => {
          const optionName = name ? ` data-option-name="${escapeHTML(name)}"` : "";
          const conditionClass = position === conditionPosition ? " is-condition" : "";
          return `<span class="pf-scroll-selected-variant__chip${conditionClass}"${optionName} data-option-position="${position}">${escapeHTML(
            optionValue
          )}</span>`;
        })
        .join("");
      summary.hidden = optionEntries.length === 0;
    });
  };

  const updatePfScrollProductBadges = (section, variant) => {
    if (!section || !variant) return;
    const price = Number(variant.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price) || 0;
    const isOnSale = compareAtPrice > price;
    const isSoldOut = variant.available === false;

    section.querySelectorAll("[data-pf-scroll-sale-badge]").forEach((badge) => {
      badge.classList.toggle("hidden", !isOnSale);
    });
    section.querySelectorAll("[data-pf-scroll-sold-out-badge]").forEach((badge) => {
      badge.classList.toggle("hidden", !isSoldOut);
    });
  };

  const formatPfScrollMoney = (cents) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return "";
    if (typeof window.Shopify?.utils?.formatMoney === "function") {
      return window.Shopify.utils.formatMoney(amount);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "USD",
      }).format(amount / 100);
    } catch {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };

  // The Afterpay app blocks (<square-placement>) keep the amount they were rendered with, so a
  // Condition switch left the old monthly price. They redraw when data-amount changes. The guided
  // template's top copy (.pf-guided-afterpay) is kept by pf-scroll-pilot-guided-steps.js.
  const updatePfScrollPaymentMessaging = (section, price) => {
    const amount = (price / 100).toFixed(2);
    section.querySelectorAll("square-placement").forEach((placement) => {
      if (placement.closest(".pf-guided-afterpay")) return;
      if (placement.getAttribute("data-amount") !== amount) {
        placement.setAttribute("data-amount", amount);
      }
    });
  };

  const updatePfScrollVisiblePrices = (section, variant) => {
    if (!section || !variant) return;
    const price = Number(variant.price);
    const compareAtPrice = Number(variant.compare_at_price ?? variant.compareAtPrice) || 0;
    if (!Number.isFinite(price)) return;
    updatePfScrollPaymentMessaging(section, price);
    const priceText = formatPfScrollMoney(price);
    const compareAtPriceText = formatPfScrollMoney(compareAtPrice);
    const isOnSale = compareAtPrice > price;
    const isSoldOut = variant.available === false;

    section.querySelectorAll(".pf-scroll-product-info__price").forEach((priceRoot) => {
      priceRoot.hidden = false;
      priceRoot.dataset.pfSelectedVariantPrice = String(price);
      priceRoot.dataset.pfSelectedVariantCompareAtPrice = String(compareAtPrice || "");
      const priceElement = priceRoot.querySelector(".price");
      if (!priceElement) return;

      priceElement.classList.toggle("price--on-sale", isOnSale);
      priceElement.classList.toggle("price--sold-out", isSoldOut);
      const regularPrice =
        priceElement.querySelector(".price__regular > .price-item.price-item--regular") ||
        priceElement.querySelector(".price__regular .price-item.price-item--regular");
      if (regularPrice) regularPrice.textContent = priceText;
      priceElement.querySelectorAll(".price__sale .price-item--sale").forEach((salePrice) => {
        salePrice.textContent = priceText;
      });
      priceElement
        .querySelectorAll(".price__sale s.price-item--regular")
        .forEach((comparePrice) => {
          comparePrice.textContent = compareAtPriceText;
        });

      let discountBadge = priceElement.querySelector(".price__badge-discount");
      if (isOnSale && !discountBadge) {
        discountBadge = document.createElement("span");
        discountBadge.className = "badge price__badge-discount";
        priceElement.appendChild(discountBadge);
      }
      if (discountBadge) {
        discountBadge.hidden = !isOnSale;
        if (isOnSale) {
          discountBadge.textContent = `Save ${Math.round(
            ((compareAtPrice - price) * 100) / compareAtPrice
          )}%`;
        }
      }
    });
  };

  const updatePfScrollProductFormsVariantId = (variantId) => {
    if (!variantId) return;
    document
      .querySelectorAll('[data-pf-scroll-product-info] form input[name="id"]')
      .forEach((input) => {
        input.value = variantId;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
  };

  const updatePfScrollBuyButtons = (html, sourceSectionId, variant) => {
    const sourceButton =
      html?.getElementById?.(`ProductSubmitButton-${sourceSectionId}`) ||
      html?.querySelector?.('button[id^="ProductSubmitButton-"]');
    const sourceInput = html?.querySelector?.('form input[name="id"]');
    const isUnavailable = !variant;
    const isDisabled =
      sourceButton?.hasAttribute("disabled") ?? (isUnavailable || variant?.available === false);
    const disabledText =
      sourceButton?.querySelector("span")?.textContent?.trim() ||
      (isUnavailable ? window.variantStrings.unavailable : window.variantStrings.soldOut);

    document
      .querySelectorAll("[data-pf-scroll-product-info] pf-pilot-product-form")
      .forEach((productForm) => {
        const form = productForm.querySelector("form");
        const input = form?.querySelector('input[name="id"]');
        const button = productForm.querySelector('button[type="submit"][name="add"]');
        const buttonText = button?.querySelector("span");

        if (input) {
          input.disabled = sourceInput?.disabled ?? isDisabled;
        }

        if (typeof productForm.toggleSubmitButton === "function") {
          productForm.toggleSubmitButton(isDisabled, disabledText);
          return;
        }

        button?.toggleAttribute("disabled", isDisabled);
        if (buttonText) {
          buttonText.textContent = isDisabled ? disabledText : window.variantStrings.addToCart;
        }
      });
  };

  const updatePfScrollSelectedVariantImages = (section, variant) => {
    if (!section || !variant?.id) return;
    section.querySelectorAll("[data-pf-selected-variant-image-root]").forEach((root) => {
      const mapScript = root.querySelector("[data-pf-selected-variant-image-map]");
      const image = root.querySelector(".pf-selected-variant-image__img");
      if (!mapScript || !image) return;

      let imageMap;
      try {
        imageMap = JSON.parse(mapScript.textContent || "{}");
      } catch {
        return;
      }

      const nextImage = imageMap[String(variant.id)];
      const fallbackSrc =
        variant.featured_media?.preview_image?.src || variant.featured_image?.src || "";
      const nextSrc = nextImage?.src || fallbackSrc;
      if (!nextSrc) return;

      image.src = nextSrc;
      if (nextImage?.srcset) {
        image.srcset = nextImage.srcset;
      } else {
        image.removeAttribute("srcset");
      }
      if (nextImage?.alt || variant.featured_media?.alt)
        image.alt = nextImage?.alt || variant.featured_media.alt;
      root.dataset.currentVariantId = String(variant.id);
    });
  };

  const getPfScrollProductId = (section) =>
    section?.querySelector?.("[data-pf-scroll-product-info]")?.dataset?.pfSelectedProductId ||
    section?.querySelector?.("[data-pf-adaptive-options]")?.dataset?.pfProductId ||
    section?.querySelector?.("[data-pf-scroll-complementary-selector]")?.dataset?.productId ||
    "";

  const getPfScrollRenderedProductId = (section) =>
    section?.querySelector?.("[data-pf-adaptive-options]")?.dataset?.pfProductId || "";

  const getPfScrollImageSource = (image) => {
    if (!image) return "";
    if (typeof image === "string") return image;
    return image.preview_image?.src || image.src || image.url || "";
  };

  const getPfScrollImageAlt = (image) => {
    if (!image || typeof image === "string") return "";
    return image.alt || image.preview_image?.alt || "";
  };

  const getPfScrollMediaId = (media) => {
    if (!media || typeof media === "string") return "";
    return media.id ? String(media.id) : "";
  };

  const getPfScrollVariantImageMapEntry = (section, variantId) => {
    const mapScript = section?.querySelector?.("[data-pf-selected-variant-image-map]");
    if (!mapScript || !variantId) return null;
    try {
      return JSON.parse(mapScript.textContent || "{}")[String(variantId)] || null;
    } catch {
      return null;
    }
  };

  const buildPfScrollImageSrcset = (src) => {
    if (!src) return "";
    try {
      const widths = [375, 550, 750, 1100, 1500, 1600];
      return widths
        .map((width) => {
          const url = new URL(src, window.location.origin);
          url.searchParams.set("width", String(width));
          return `${url.toString()} ${width}w`;
        })
        .join(", ");
    } catch {
      return "";
    }
  };

  const updatePfScrollAdaptiveImageElements = (section, imageData, variantId) => {
    if (!section || !imageData?.src) return;
    const adaptiveRoot = section.querySelector("[data-pf-adaptive-options]");
    if (!adaptiveRoot) return;

    const imageBlocks = new Set(adaptiveRoot.querySelectorAll(".pf-product-scroll__image-block"));
    section
      .querySelectorAll(`${selectors.mediaPanel} .pf-product-scroll__image-block`)
      .forEach((imageBlock) => imageBlocks.add(imageBlock));

    imageBlocks.forEach((imageBlock) => {
      const mediaId = String(imageData.mediaId || "");
      const mediaTemplate = Array.from(
        imageBlock.querySelectorAll("template[data-pf-selected-media-template]")
      ).find((template) => template.dataset.pfSelectedMediaTemplate === mediaId);
      const activeMedia = imageBlock.querySelector("[data-pf-selected-media-active]");
      if (mediaTemplate?.content && activeMedia) {
        activeMedia.replaceChildren(mediaTemplate.content.cloneNode(true));
        imageBlock.dataset.currentVariantId = String(variantId);
        syncPfScrollGalleryMainModals();
        return;
      }

      const image = imageBlock.querySelector(".pf-product-scroll__img");
      if (!image) return;
      image.src = imageData.src;
      const srcset = imageData.srcset || buildPfScrollImageSrcset(imageData.src);
      if (srcset) image.srcset = srcset;
      else image.removeAttribute("srcset");
      if (imageData.alt) image.alt = imageData.alt;
      imageBlock.dataset.currentVariantId = String(variantId);
    });
  };

  const getPfScrollComparableImagePath = (src) => {
    if (!src) return "";
    try {
      return new URL(src, window.location.origin).pathname;
    } catch {
      return "";
    }
  };

  const activatePfScrollAdaptiveGalleryMedia = (section, mediaId, mediaSrc = "") => {
    if (!section || (!mediaId && !mediaSrc)) return;
    const mediaPath = getPfScrollComparableImagePath(mediaSrc);
    section.querySelectorAll(".pf-product-gallery--theme").forEach((galleryRoot) => {
      const mediaItems = Array.from(
        galleryRoot.querySelectorAll(".product__media-item[data-media-id]")
      );
      const target = mediaItems.find((item) => {
        if (mediaId && String(item.dataset.mediaId || "").endsWith(`-${mediaId}`)) return true;
        if (!mediaPath) return false;
        const image = item.querySelector("img");
        return getPfScrollComparableImagePath(image?.currentSrc || image?.src) === mediaPath;
      });
      if (!target) return;
      if (target.classList.contains("is-active")) return;

      const mediaGallery =
        target.closest("media-gallery") || galleryRoot.querySelector("media-gallery");
      if (typeof mediaGallery?.setActiveMedia === "function") {
        try {
          mediaGallery.setActiveMedia(target.dataset.mediaId, false);
          return;
        } catch {
          // A cloned gallery can upgrade before its controls do; keep the visible slide in sync.
        }
      }

      const viewer = target.closest('[id^="Slider-Gallery-"]') || galleryRoot;
      const slides = Array.from(viewer.querySelectorAll(".product__media-item.slider__slide"));
      slides.forEach((slide) => slide.classList.toggle("is-active", slide === target));
      const activeIndex = slides.indexOf(target);
      galleryRoot.querySelectorAll(".pf-theme-gallery__segment").forEach((segment, index) => {
        segment.classList.toggle("is-active", index === activeIndex);
      });
    });
  };

  const pfScrollProductJsonCache = new Map();

  const getPfScrollProductJsonUrl = (section) => {
    const productUrl = getPfScrollProductInfo(section)?.dataset?.url;
    if (!productUrl) return "";
    try {
      const url = new URL(productUrl, window.location.origin);
      url.search = "";
      if (!url.pathname.endsWith(".js")) {
        url.pathname = `${url.pathname.replace(/\/$/, "")}.js`;
      }
      return url.toString();
    } catch {
      return "";
    }
  };

  const fetchPfScrollProductJson = (url) => {
    if (!url) return Promise.resolve(null);
    if (!pfScrollProductJsonCache.has(url)) {
      const request = fetch(url, { headers: { Accept: "application/json" } })
        .then((response) => {
          if (!response.ok) throw new Error(`Product JSON request failed: ${response.status}`);
          return response.json();
        })
        .catch(() => {
          pfScrollProductJsonCache.delete(url);
          return null;
        });
      pfScrollProductJsonCache.set(url, request);
    }
    return pfScrollProductJsonCache.get(url);
  };

  const getPfScrollVariantMediaData = (section, variant, productData = null) => {
    const variantId = String(variant?.id || "");
    const productVariant = productData?.variants?.find(
      (candidate) => String(candidate.id) === variantId
    );
    const imageMapEntry = getPfScrollVariantImageMapEntry(section, variantId);
    const selectedMedia =
      variant?.featured_media ||
      variant?.featured_image ||
      productVariant?.featured_media ||
      productVariant?.featured_image;
    const featuredMedia = productData?.featured_media || productData?.featured_image;
    const src =
      getPfScrollImageSource(selectedMedia) ||
      imageMapEntry?.src ||
      getPfScrollImageSource(featuredMedia);
    const alt =
      getPfScrollImageAlt(selectedMedia) ||
      imageMapEntry?.alt ||
      getPfScrollImageAlt(featuredMedia) ||
      productData?.title ||
      "";
    const mediaId =
      getPfScrollMediaId(selectedMedia) ||
      String(imageMapEntry?.mediaId || "") ||
      getPfScrollMediaId(featuredMedia);

    return { src, srcset: imageMapEntry?.srcset || "", alt, mediaId };
  };

  const syncPfScrollAdaptiveSelectedVariantMedia = (section, variant) => {
    if (!section || !variant?.id) return;
    const variantId = String(variant.id);
    const productId = getPfScrollProductId(section);
    const productJsonUrl = getPfScrollProductJsonUrl(section);
    const immediateMedia = getPfScrollVariantMediaData(section, variant);

    updatePfScrollAdaptiveImageElements(section, immediateMedia, variantId);
    activatePfScrollAdaptiveGalleryMedia(section, immediateMedia.mediaId, immediateMedia.src);

    fetchPfScrollProductJson(productJsonUrl).then((productData) => {
      if (!productData || !section.isConnected) return;
      const adaptiveRoot = section.querySelector("[data-pf-adaptive-options]");
      if (adaptiveRoot?.dataset?.pfSelectedVariantId !== variantId) return;
      if (productId && getPfScrollProductId(section) !== productId) return;
      if (getPfScrollProductJsonUrl(section) !== productJsonUrl) return;
      if (productId && productData.id && String(productData.id) !== productId) return;

      const resolvedMedia = getPfScrollVariantMediaData(section, variant, productData);
      updatePfScrollAdaptiveImageElements(section, resolvedMedia, variantId);
      activatePfScrollAdaptiveGalleryMedia(section, resolvedMedia.mediaId, resolvedMedia.src);
    });
  };

  const pfScrollProductContextRebuilds = new WeakMap();

  const getPfScrollSectionId = (section) => {
    const productInfoSectionId = getPfScrollProductInfo(section)?.dataset?.section;
    if (productInfoSectionId) return productInfoSectionId;
    const sectionRoot = section?.closest?.('[id^="shopify-section-"]');
    return sectionRoot?.id?.replace(/^shopify-section-/, "") || "";
  };

  const getPfScrollComparableProductUrl = (value) => {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      return url.pathname.replace(/\/$/, "");
    } catch {
      return "";
    }
  };

  const scopePfScrollRecommendationsForProduct = (section, productId) => {
    if (!section || !productId) return;
    section
      .querySelectorAll("[data-pf-scroll-complementary-selector]")
      .forEach((recommendations) => {
        if (String(recommendations.dataset.productId || "") === productId) return;
        recommendations.observer?.unobserve?.(recommendations);
        recommendations.innerHTML = "";
        recommendations.dataset.productId = productId;
        delete recommendations.dataset.pfScrollEagerLoaded;
        recommendations.dataset.pfScrollComplementaryStatus = "loading";
      });
  };

  const setPfScrollProductContextLoading = (section, isLoading) => {
    const blocks = section?.querySelector?.(".pf-product-scroll__blocks");
    const layout = section?.querySelector?.(".pf-product-scroll__layout");
    if (isLoading) {
      blocks?.setAttribute("aria-busy", "true");
      if (layout) {
        layout.dataset.pfProductContextLoading = "true";
        layout.hidden = true;
      }
      return;
    }
    blocks?.removeAttribute("aria-busy");
    if (layout?.dataset?.pfProductContextLoading === "true") {
      delete layout.dataset.pfProductContextLoading;
      layout.hidden = false;
    }
  };

  const getPfScrollProductContext = (section, variant = null) => {
    const productInfo = getPfScrollProductInfo(section);
    const selectedProductUrl = Array.from(
      getProductInfoVariantSelects(section)?.querySelectorAll?.(
        'input[type="radio"]:checked[data-product-url]'
      ) || []
    ).find((input) => input.dataset.productUrl)?.dataset?.productUrl;
    const productId = String(
      variant?.product_id || variant?.productId || productInfo?.dataset?.pfSelectedProductId || ""
    );
    const variantId = String(variant?.id || productInfo?.dataset?.pfSelectedVariantId || "");
    const productUrl = selectedProductUrl || productInfo?.dataset?.url || "";
    return { productId, variantId, productUrl };
  };

  const pfScrollPendingVariantFocus = new WeakMap();

  const getPfScrollVariantFocusToken = (section, candidate = document.activeElement) => {
    if (
      !section ||
      !(candidate instanceof HTMLInputElement) ||
      candidate.type !== "radio" ||
      !section.contains(candidate)
    ) {
      return null;
    }

    const target = candidate.closest(selectors.variantOption);
    if (!target) return null;

    return {
      blockId: target.dataset.variantOptionBlock || "",
      optionPosition: Number(
        target.dataset.optionPosition || target.dataset.variantOptionIndex || 0
      ),
      optionValueId: candidate.dataset.optionValueId || "",
      value: candidate.value || "",
    };
  };

  const pfScrollVariantFocusTokensMatch = (left, right) =>
    Boolean(left && right) &&
    left.blockId === right.blockId &&
    left.optionPosition === right.optionPosition &&
    ((left.optionValueId && right.optionValueId && left.optionValueId === right.optionValueId) ||
      left.value === right.value);

  const stashPfScrollVariantFocus = (section, candidate = document.activeElement) => {
    const token = getPfScrollVariantFocusToken(section, candidate);
    if (token) {
      pfScrollPendingVariantFocus.set(section, token);
      return token;
    }

    const focusIsUnowned =
      !candidate || candidate === document.body || candidate === document.documentElement;
    const focusIsNativeVariantSource =
      candidate instanceof HTMLInputElement &&
      candidate.type === "radio" &&
      Boolean(candidate.closest('variant-selects:not([data-upsell-isolated="true"])'));
    if (!focusIsUnowned && !focusIsNativeVariantSource) {
      pfScrollPendingVariantFocus.delete(section);
      return null;
    }

    return pfScrollPendingVariantFocus.get(section) || null;
  };

  const restorePfScrollVariantFocus = (
    section,
    token = pfScrollPendingVariantFocus.get(section)
  ) => {
    if (!section || !token) return false;

    const { activeElement } = document;
    const activeToken = getPfScrollVariantFocusToken(section, activeElement);
    const focusIsNativeVariantSource =
      activeElement instanceof HTMLInputElement &&
      activeElement.type === "radio" &&
      Boolean(activeElement.closest('variant-selects:not([data-upsell-isolated="true"])'));
    const focusIsUnowned =
      !activeElement ||
      activeElement === document.body ||
      activeElement === document.documentElement ||
      focusIsNativeVariantSource;

    if (!focusIsUnowned && !activeToken) {
      pfScrollPendingVariantFocus.delete(section);
      return false;
    }
    if (activeToken) {
      const focusMatches = pfScrollVariantFocusTokensMatch(activeToken, token);
      if (!focusMatches) pfScrollPendingVariantFocus.delete(section);
      return focusMatches;
    }

    const targets = Array.from(section.querySelectorAll(selectors.variantOption));
    const target =
      targets.find(
        (candidate) => token.blockId && candidate.dataset.variantOptionBlock === token.blockId
      ) ||
      targets.find(
        (candidate) =>
          token.optionPosition &&
          Number(candidate.dataset.optionPosition || candidate.dataset.variantOptionIndex || 0) ===
            token.optionPosition
      );
    if (!target) return false;

    const inputs = Array.from(target.querySelectorAll('input[type="radio"]'));
    const input =
      inputs.find(
        (candidate) =>
          token.optionValueId && candidate.dataset.optionValueId === token.optionValueId
      ) || inputs.find((candidate) => candidate.value === token.value);
    if (!input || input.disabled || input.getAttribute("aria-disabled") === "true") return false;

    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
    if (document.activeElement !== input) return false;

    pfScrollPendingVariantFocus.set(section, token);
    return true;
  };

  const pfScrollPendingOptionSelections = new WeakMap();
  let pfScrollPendingOptionSelectionCarry = null;

  const stagePfScrollOptionAnalytics = (
    section,
    { optionName, optionPosition, newValue, previousValue }
  ) => {
    if (!section || !optionName || !optionPosition || !newValue) return;
    const pendingSelection = {
      optionName,
      optionPosition: Number(optionPosition),
      newValue,
      previousValue: previousValue || "",
      previousProductId: String(getPfScrollProductId(section) || ""),
    };
    pfScrollPendingOptionSelections.set(section, pendingSelection);
    // Full-main combined-listing swaps replace the section node. Retain the
    // latest unresolved selection so the new live section emits exactly once.
    pfScrollPendingOptionSelectionCarry = pendingSelection;
  };

  const emitResolvedPfScrollOptionAnalytics = (variant) => {
    if (!variant?.id) {
      getLivePfScrollSections().forEach((section) => {
        pfScrollPendingOptionSelections.delete(section);
      });
      pfScrollPendingOptionSelectionCarry = null;
      return;
    }

    getLivePfScrollSections().forEach((section) => {
      const pending =
        pfScrollPendingOptionSelections.get(section) || pfScrollPendingOptionSelectionCarry;
      if (!pending) return;
      const resolvedOptionValue =
        variant?.options?.[pending.optionPosition - 1] ??
        variant?.[`option${pending.optionPosition}`];
      if (String(resolvedOptionValue ?? "") !== String(pending.newValue)) return;

      const resolvedProductId = String(
        variant.product_id || variant.productId || getPfScrollProductId(section) || ""
      );
      const detail = {
        resolved_product_id: resolvedProductId,
        resolved_variant_id: String(variant.id),
        option_name: pending.optionName,
        option_position: pending.optionPosition,
        new_value: pending.newValue,
        previous_value: pending.previousValue,
        source: "pf_scroll",
        pdp_experience: "pf_scroll_pilot",
      };

      if (pending.previousProductId && pending.previousProductId !== resolvedProductId) {
        detail.previous_product_id = pending.previousProductId;
      }

      // Delete first so synchronous listeners can never cause a duplicate emission.
      pfScrollPendingOptionSelections.delete(section);
      if (pending === pfScrollPendingOptionSelectionCarry) {
        pfScrollPendingOptionSelectionCarry = null;
      }
      section.dispatchEvent(
        new CustomEvent("plug:pdp-option-selected", {
          bubbles: true,
          detail,
        })
      );
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "plug_pdp_option_selected",
        ...detail,
      });
    });
  };

  /**
   * Combined listings can replace only <product-info>, leaving adaptive rows rendered for the
   * previous child. Pull the selected child's PF section so option rows and product integrations
   * are rebuilt from one server-rendered product context.
   */
  const schedulePfScrollProductContextRebuild = (section, variant = null) => {
    if (!section?.isConnected) return false;
    const renderedProductId = String(getPfScrollRenderedProductId(section));
    const { productId, variantId, productUrl } = getPfScrollProductContext(section, variant);
    const existingRequest = pfScrollProductContextRebuilds.get(section);

    if (!renderedProductId || !productId || renderedProductId === productId) {
      existingRequest?.controller.abort();
      pfScrollProductContextRebuilds.delete(section);
      setPfScrollProductContextLoading(section, false);
      return false;
    }

    setPfScrollProductContextLoading(section, true);
    scopePfScrollRecommendationsForProduct(section, productId);
    const sectionId = getPfScrollSectionId(section);
    if (!sectionId || !productUrl) {
      setPfScrollProductContextLoading(section, false);
      return false;
    }

    const selectedProductUrl = new URL(productUrl, window.location.origin);
    const productInfo = getPfScrollProductInfo(section);
    if (productInfo) {
      productInfo.dataset.url = `${selectedProductUrl.pathname}${selectedProductUrl.search}`;
      productInfo.dataset.pfSelectedProductId = productId;
      productInfo.dataset.pfSelectedVariantId = variantId;
    }

    const requestUrl = new URL(selectedProductUrl.pathname, window.location.origin);
    selectedProductUrl.searchParams.forEach((value, key) => {
      requestUrl.searchParams.set(key, value);
    });
    const previewThemeId = getPfScrollPreviewThemeId();
    if (previewThemeId) requestUrl.searchParams.set("preview_theme_id", previewThemeId);
    requestUrl.searchParams.set("section_id", sectionId);
    if (variantId) requestUrl.searchParams.set("variant", variantId);
    const productUrlKey = getPfScrollComparableProductUrl(productUrl);
    const requestKey = `${productId}:${variantId}:${requestUrl.toString()}`;
    if (existingRequest?.key === requestKey) return true;
    existingRequest?.controller.abort();

    const controller = new AbortController();
    const requestState = { controller, key: requestKey };
    pfScrollProductContextRebuilds.set(section, requestState);

    fetch(requestUrl.toString(), {
      signal: controller.signal,
      headers: { Accept: "text/html" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`PF section request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (!section.isConnected || controller.signal.aborted) return;
        if (pfScrollProductContextRebuilds.get(section) !== requestState) return;

        const currentContext = getPfScrollProductContext(
          section,
          getPfScrollSelectedVariant(getPfScrollProductInfo(section))
        );
        if (currentContext.productId !== productId || currentContext.variantId !== variantId)
          return;
        if (getPfScrollComparableProductUrl(currentContext.productUrl) !== productUrlKey) return;

        const responseDocument = new DOMParser().parseFromString(responseText, "text/html");
        const responseSection = responseDocument.querySelector(selectors.section);
        const responseBlocks =
          responseSection?.querySelector(".pf-product-scroll__blocks") ||
          responseDocument.querySelector(".pf-product-scroll__blocks");
        const responseAdaptiveRoot = responseBlocks?.querySelector("[data-pf-adaptive-options]");
        const responseProductId = String(responseAdaptiveRoot?.dataset?.pfProductId || "");
        const currentBlocks = section.querySelector(".pf-product-scroll__blocks");
        if (!responseBlocks || !currentBlocks || responseProductId !== productId) return;

        const complementaryProductId = section.dataset.pfScrollComplementaryProductId || "";
        const complementaryVariantIds =
          complementaryProductId === productId
            ? getPfScrollComplementaryVariantIds(section)
            : new Set();

        stashPfScrollVariantFocus(section);
        variantSectionRefreshers.delete(section);
        currentBlocks.replaceWith(document.importNode(responseBlocks, true));
        setPfScrollComplementaryVariantIds(section, complementaryVariantIds, productId);
        document.dispatchEvent(
          new CustomEvent("pf:product-context-rebuilt", {
            detail: { variant: getPfScrollSelectedVariant(getPfScrollProductInfo(section)) },
          })
        );

        // eslint-disable-next-line no-use-before-define -- async rebuild completes after initialization
        onGlobalVariantChangeForProductScroll();
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          debugPfScrollVariantSelectors("product context rebuild failed", {
            productId,
            variantId,
            message: error?.message || String(error),
          });
          const fallbackUrl = new URL(selectedProductUrl.pathname, window.location.origin);
          selectedProductUrl.searchParams.forEach((value, key) => {
            fallbackUrl.searchParams.set(key, value);
          });
          if (previewThemeId) fallbackUrl.searchParams.set("preview_theme_id", previewThemeId);
          if (variantId) fallbackUrl.searchParams.set("variant", variantId);
          window.location.assign(fallbackUrl.toString());
        }
      })
      .finally(() => {
        if (pfScrollProductContextRebuilds.get(section) === requestState) {
          pfScrollProductContextRebuilds.delete(section);
          setPfScrollProductContextLoading(section, false);
        }
      });

    return true;
  };

  const syncPfScrollProductInfoState = (section) => {
    const variant = getPfScrollSelectedVariant(getPfScrollProductInfo(section));
    sanitizePfScrollSelectedVariantSummaries(section);
    if (!variant) return;
    const productId = variant.product_id || variant.productId;
    const productInfo = getPfScrollProductInfo(section);
    if (productInfo) {
      productInfo.dataset.pfSelectedVariantId = String(variant.id || "");
      productInfo.dataset.pfSelectedVariantPrice = String(variant.price ?? "");
      if (productId) productInfo.dataset.pfSelectedProductId = String(productId);
    }
    updatePfScrollVisiblePrices(section, variant);
    updatePfScrollProductBadges(section, variant);
    if (schedulePfScrollProductContextRebuild(section, variant)) return;
    section.querySelectorAll("[data-pf-adaptive-options]").forEach((adaptiveRoot) => {
      adaptiveRoot.dataset.pfSelectedVariantId = String(variant.id || "");
      adaptiveRoot.dataset.pfSelectedVariantPrice = String(variant.price ?? "");
    });
    updatePfScrollSelectedVariantSummaries(section, variant);
    updatePfScrollSelectedVariantImages(section, variant);
    syncPfScrollAdaptiveSelectedVariantMedia(section, variant);
  };

  const clearPfScrollComplementaryState = (section, productId = "") => {
    if (!section) return;
    section.dataset.pfScrollComplementaryVariantIds = "";
    if (productId) section.dataset.pfScrollComplementaryProductId = productId;
    else delete section.dataset.pfScrollComplementaryProductId;

    section.querySelectorAll(selectors.complementaryOption).forEach((option) => {
      option.classList.remove("is-selected");
      option.setAttribute("aria-pressed", "false");
    });
    section
      .querySelectorAll(`input[name="${PF_SCROLL_COMPLEMENTARY_INPUT_NAME}"]`)
      .forEach((input) => input.remove());
    section
      .querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
      .forEach((summary) => {
        const list = summary.querySelector("[data-pf-scroll-selected-complementary-list]");
        if (list) list.innerHTML = "";
        delete summary.dataset.pfScrollSelectedComplementaryIds;
        delete summary.dataset.pfScrollFallbackComplementaryIds;
        delete summary.dataset.pfScrollFallbackComplementaryHtml;
        delete summary.dataset.pfScrollRenderedFromFallback;
        if (productId) summary.dataset.pfScrollComplementaryProductId = productId;
        else delete summary.dataset.pfScrollComplementaryProductId;
        summary.hidden = true;
      });
  };

  const ensurePfScrollComplementaryProductScope = (section) => {
    if (!section) return "";
    const productId = getPfScrollProductId(section);
    const storedProductId = section.dataset.pfScrollComplementaryProductId || "";
    if (productId && storedProductId && storedProductId !== productId) {
      clearPfScrollComplementaryState(section, productId);
    } else if (productId) {
      section.dataset.pfScrollComplementaryProductId = productId;
    }
    return productId;
  };

  const getPfScrollComplementaryVariantIds = (section) => {
    ensurePfScrollComplementaryProductScope(section);
    return new Set(
      (section?.dataset?.pfScrollComplementaryVariantIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
    );
  };

  const setPfScrollComplementaryVariantIds = (section, ids, productId = "") => {
    if (!section) return;
    const scopedProductId = productId || ensurePfScrollComplementaryProductScope(section);
    section.dataset.pfScrollComplementaryVariantIds = Array.from(ids).join(",");
    if (scopedProductId) section.dataset.pfScrollComplementaryProductId = scopedProductId;
    debugPfScrollAddOns("set selected ids on section", {
      sectionId: getPfScrollDebugSectionId(section),
      productId: scopedProductId,
      ids: Array.from(ids),
    });
  };

  const getPfScrollComplementarySummarySnapshot = (section) => {
    const summary = section?.querySelector("[data-pf-scroll-selected-complementary-summary]");
    const list = summary?.querySelector("[data-pf-scroll-selected-complementary-list]");
    const ids =
      summary?.dataset?.pfScrollSelectedComplementaryIds ||
      section?.dataset?.pfScrollComplementaryVariantIds;

    if (!ids || !list?.children.length) return null;

    return {
      ids,
      html: list.innerHTML,
      productId: summary.dataset.pfScrollComplementaryProductId || getPfScrollProductId(section),
    };
  };

  /** Captures the already-loaded complementary option list so a same-product re-render can't drop it. */
  const getPfScrollComplementarySelectorSnapshot = (section) => {
    const recommendations = Array.from(
      section?.querySelectorAll("[data-pf-scroll-complementary-selector]") || []
    ).find((rec) => rec.querySelector(selectors.complementaryOption));
    if (!recommendations) return null;

    return { html: recommendations.innerHTML, productId: recommendations.dataset.productId || "" };
  };

  const collectPfScrollComplementaryVariantIds = (section) => {
    const ids = getPfScrollComplementaryVariantIds(section);
    const optionIds = [];
    const inputIds = [];

    section
      ?.querySelectorAll(
        `${selectors.complementaryOption}.is-selected, ${selectors.complementaryOption}[aria-pressed="true"]`
      )
      .forEach((option) => {
        if (!option.dataset.variantId) return;
        ids.add(option.dataset.variantId);
        optionIds.push(option.dataset.variantId);
      });

    section
      ?.querySelectorAll(`input[name="${PF_SCROLL_COMPLEMENTARY_INPUT_NAME}"]`)
      .forEach((input) => {
        if (!input.value) return;
        ids.add(input.value);
        inputIds.push(input.value);
      });

    debugPfScrollAddOns("collect selected ids", {
      sectionId: getPfScrollDebugSectionId(section),
      isLive: isLivePfScrollSection(section),
      datasetIds: Array.from(getPfScrollComplementaryVariantIds(section)),
      optionIds,
      inputIds,
      resolvedIds: Array.from(ids),
    });

    return ids;
  };

  const preservePfScrollComplementaryStateForLiveSections = () => {
    const allSections = getPfScrollSections();
    const liveSections = getLivePfScrollSections();
    const stateByProductId = new Map();

    allSections.forEach((section) => {
      const productId = ensurePfScrollComplementaryProductScope(section);
      if (!productId) return;
      const state = stateByProductId.get(productId) || {
        selectedIds: new Set(),
        summarySnapshot: null,
        selectorSnapshot: null,
      };
      const summarySnapshot = getPfScrollComplementarySummarySnapshot(section);
      const selectorSnapshot = getPfScrollComplementarySelectorSnapshot(section);
      if (!state.summarySnapshot && summarySnapshot?.productId === productId) {
        state.summarySnapshot = summarySnapshot;
      }
      if (!state.selectorSnapshot && selectorSnapshot?.productId === productId) {
        state.selectorSnapshot = selectorSnapshot;
      }
      collectPfScrollComplementaryVariantIds(section).forEach((variantId) =>
        state.selectedIds.add(variantId)
      );
      stateByProductId.set(productId, state);
    });

    debugPfScrollAddOns("preserve after variant swap", {
      allSectionIds: allSections.map(getPfScrollDebugSectionId),
      liveSectionIds: liveSections.map(getPfScrollDebugSectionId),
      productIds: Array.from(stateByProductId.keys()),
    });

    liveSections.forEach((section) => {
      const productId = ensurePfScrollComplementaryProductScope(section);
      const state = stateByProductId.get(productId);
      const selectorSnapshot = state?.selectorSnapshot;
      const summarySnapshot = state?.summarySnapshot;

      // Re-apply the loaded list only when the rendered product still matches. A combined-listing
      // product swap must fetch its own recommendations and begin with no selected accessories.
      if (selectorSnapshot) {
        section
          .querySelectorAll("[data-pf-scroll-complementary-selector]")
          .forEach((recommendations) => {
            if (recommendations.querySelector(selectors.complementaryOption)) return;
            if ((recommendations.dataset.productId || "") !== selectorSnapshot.productId) return;
            recommendations.innerHTML = selectorSnapshot.html;
            recommendations.dataset.pfScrollEagerLoaded = "true";
            recommendations.dataset.pfScrollComplementaryStatus = "ready";
            recommendations.observer?.unobserve?.(recommendations);
          });
      }

      const selectedIds = state?.selectedIds || new Set();
      setPfScrollComplementaryVariantIds(section, selectedIds, productId);
      section
        .querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
        .forEach((summary) => {
          summary.dataset.pfScrollComplementaryProductId = productId;
          if (summarySnapshot && selectedIds.size) {
            summary.dataset.pfScrollFallbackComplementaryIds = summarySnapshot.ids;
            summary.dataset.pfScrollFallbackComplementaryHtml = summarySnapshot.html;
          } else {
            delete summary.dataset.pfScrollFallbackComplementaryIds;
            delete summary.dataset.pfScrollFallbackComplementaryHtml;
            delete summary.dataset.pfScrollRenderedFromFallback;
          }
        });
    });
  };

  const clonePfScrollComplementarySummaryPart = (option, selector, className) => {
    const source = option.querySelector(selector);
    const clone = source?.cloneNode(true) || document.createElement("span");
    clone.className = className;
    return clone;
  };

  const buildPfScrollComplementarySummaryItem = (option) => {
    const item = document.createElement("div");
    item.className = "pf-scroll-selected-complementary-products__item";
    item.appendChild(
      clonePfScrollComplementarySummaryPart(
        option,
        ".pf-scroll-complementary-selector__media",
        "pf-scroll-selected-complementary-products__media"
      )
    );
    item.appendChild(
      clonePfScrollComplementarySummaryPart(
        option,
        ".pf-scroll-complementary-selector__content",
        "pf-scroll-selected-complementary-products__content"
      )
    );
    item.appendChild(
      clonePfScrollComplementarySummaryPart(
        option,
        ".pf-scroll-complementary-selector__price",
        "pf-scroll-selected-complementary-products__price"
      )
    );
    return item;
  };

  const syncPfScrollComplementarySummary = (section, selectedIds) => {
    const productId = ensurePfScrollComplementaryProductScope(section);
    const selectedOptions = Array.from(
      section.querySelectorAll(selectors.complementaryOption)
    ).filter((option) => selectedIds.has(option.dataset.variantId));
    const selectedKey = selectedOptions.length
      ? selectedOptions.map((option) => option.dataset.variantId).join(",")
      : Array.from(selectedIds).join(",");
    const summaries = Array.from(
      section.querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
    );

    debugPfScrollAddOns("sync summary start", {
      sectionId: getPfScrollDebugSectionId(section),
      selectedIds: Array.from(selectedIds),
      selectedOptionIds: selectedOptions.map((option) => option.dataset.variantId),
      optionCount: section.querySelectorAll(selectors.complementaryOption).length,
      summaryCount: summaries.length,
      selectedKey,
    });

    summaries.forEach((summary) => {
      const list = summary.querySelector("[data-pf-scroll-selected-complementary-list]");
      if (!list) return;
      const summaryProductId = summary.dataset.pfScrollComplementaryProductId || "";
      if (summaryProductId && productId && summaryProductId !== productId) {
        list.innerHTML = "";
        delete summary.dataset.pfScrollSelectedComplementaryIds;
        delete summary.dataset.pfScrollFallbackComplementaryIds;
        delete summary.dataset.pfScrollFallbackComplementaryHtml;
        delete summary.dataset.pfScrollRenderedFromFallback;
      }
      if (productId) summary.dataset.pfScrollComplementaryProductId = productId;
      const fallbackHtml = summary.dataset.pfScrollFallbackComplementaryHtml || "";
      const fallbackProductId = summary.dataset.pfScrollComplementaryProductId || "";
      const hasFallbackSummary =
        selectedIds.size > 0 &&
        selectedOptions.length === 0 &&
        fallbackHtml &&
        (!productId || fallbackProductId === productId);
      debugPfScrollAddOns("sync summary branch", {
        sectionId: getPfScrollDebugSectionId(section),
        selectedKey,
        previousKey: summary.dataset.pfScrollSelectedComplementaryIds || "",
        listChildCount: list.children.length,
        hasFallbackSummary: Boolean(hasFallbackSummary),
        renderedFromFallback: summary.dataset.pfScrollRenderedFromFallback === "true",
        hiddenBefore: summary.hidden,
      });
      if (
        summary.dataset.pfScrollSelectedComplementaryIds === selectedKey &&
        ((selectedOptions.length > 0 &&
          list.children.length === selectedOptions.length &&
          summary.dataset.pfScrollRenderedFromFallback !== "true") ||
          (hasFallbackSummary && list.children.length > 0) ||
          (!selectedIds.size && list.children.length === 0))
      ) {
        summary.hidden = selectedIds.size === 0;
        return;
      }

      summary.dataset.pfScrollSelectedComplementaryIds = selectedKey;
      list.innerHTML = "";
      if (selectedOptions.length > 0) {
        selectedOptions.forEach((option) => {
          list.appendChild(buildPfScrollComplementarySummaryItem(option));
        });
        summary.dataset.pfScrollFallbackComplementaryHtml = list.innerHTML;
        summary.dataset.pfScrollFallbackComplementaryIds = selectedKey;
        if (productId) summary.dataset.pfScrollComplementaryProductId = productId;
        delete summary.dataset.pfScrollRenderedFromFallback;
        summary.hidden = false;
        debugPfScrollAddOns("summary rendered from current options", {
          sectionId: getPfScrollDebugSectionId(section),
          selectedKey,
          itemCount: list.children.length,
        });
        return;
      }

      if (hasFallbackSummary) {
        list.innerHTML = fallbackHtml;
        summary.dataset.pfScrollRenderedFromFallback = "true";
        summary.hidden = false;
        debugPfScrollAddOns("summary rendered from fallback", {
          sectionId: getPfScrollDebugSectionId(section),
          fallbackIds: summary.dataset.pfScrollFallbackComplementaryIds || "",
          itemCount: list.children.length,
        });
        return;
      }

      delete summary.dataset.pfScrollRenderedFromFallback;
      summary.hidden = true;
      debugPfScrollAddOns("summary hidden", {
        sectionId: getPfScrollDebugSectionId(section),
        selectedIds: Array.from(selectedIds),
        selectedOptionCount: selectedOptions.length,
      });
    });
  };

  const syncPfScrollComplementarySelectorState = (section) => {
    if (!section) return;
    const productId = ensurePfScrollComplementaryProductScope(section);
    let selectedIds = getPfScrollComplementaryVariantIds(section);
    const options = Array.from(section.querySelectorAll(selectors.complementaryOption));
    if (options.length) {
      const renderedIds = new Set(
        options.map((option) => option.dataset.variantId).filter(Boolean)
      );
      selectedIds = new Set(Array.from(selectedIds).filter((id) => renderedIds.has(id)));
      setPfScrollComplementaryVariantIds(section, selectedIds, productId);
    }
    const productInfo = getPfScrollProductInfo(section);
    const forms = Array.from(productInfo?.querySelectorAll("pf-pilot-product-form form") || []);

    debugPfScrollAddOns("sync selector state start", {
      sectionId: getPfScrollDebugSectionId(section),
      isLive: isLivePfScrollSection(section),
      selectedIds: Array.from(selectedIds),
      optionIds: options.map((option) => option.dataset.variantId).filter(Boolean),
      summaryCount: section.querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
        .length,
      formCount: forms.length,
    });

    options.forEach((option) => {
      const isSelected = selectedIds.has(option.dataset.variantId);
      option.classList.toggle("is-selected", isSelected);
      option.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });

    syncPfScrollComplementarySummary(section, selectedIds);

    const selectedIdList = Array.from(selectedIds);
    forms.forEach((form) => {
      const currentInputs = Array.from(
        form.querySelectorAll(`input[name="${PF_SCROLL_COMPLEMENTARY_INPUT_NAME}"]`)
      );
      const currentValues = currentInputs.map((input) => input.value);
      if (currentValues.join(",") === selectedIdList.join(",")) return;

      form
        .querySelectorAll(`input[name="${PF_SCROLL_COMPLEMENTARY_INPUT_NAME}"]`)
        .forEach((input) => input.remove());
      selectedIdList.forEach((variantId) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = PF_SCROLL_COMPLEMENTARY_INPUT_NAME;
        input.value = variantId;
        input.dataset.pfScrollComplementaryInput = "true";
        form.appendChild(input);
      });
      debugPfScrollAddOns("form hidden inputs synced", {
        sectionId: getPfScrollDebugSectionId(section),
        formId: form.id,
        selectedIds: selectedIdList,
      });
    });
  };

  const syncPfScrollComplementaryRowVisibility = (section) => {
    if (!section?.querySelectorAll) return;
    const rows = Array.from(section.querySelectorAll(selectors.row));
    rows
      .filter((row) => row.querySelector("[data-pf-scroll-complementary-selector]"))
      .forEach((row) => {
        const recommendations = Array.from(
          row.querySelectorAll("[data-pf-scroll-complementary-selector]")
        );
        const shouldHide =
          recommendations.length > 0 &&
          recommendations.every((recommendation) => {
            const hasOptions = Boolean(recommendation.querySelector(selectors.complementaryOption));
            if (hasOptions) {
              recommendation.dataset.pfScrollComplementaryStatus = "ready";
              return false;
            }

            const status = recommendation.dataset.pfScrollComplementaryStatus;
            const loadedEmpty =
              recommendation.dataset.pfScrollEagerLoaded === "true" && status !== "loading";
            if (loadedEmpty && status !== "empty") {
              recommendation.dataset.pfScrollComplementaryStatus = "empty";
            }
            return true;
          });
        const mediaItem = section.querySelector(
          `${selectors.mediaPanel} ${selectors.mediaItem}[data-block-index="${rows.indexOf(row)}"]`
        );

        if (row.hidden !== shouldHide) row.hidden = shouldHide;
        if (mediaItem && mediaItem.hidden !== shouldHide) mediaItem.hidden = shouldHide;
      });
  };

  const syncPfPilotBoostOfferRowVisibility = (section) => {
    if (!section?.querySelectorAll) return;
    const rows = Array.from(section.querySelectorAll(selectors.row));
    rows
      .filter((row) => row.matches("[data-pf-boost-row]"))
      .forEach((row) => {
        const offer = row.querySelector("[data-pf-pilot-boost-offer]");
        const shouldShow = offer?.dataset.pfBoostOfferEligible === "true";
        row.hidden = !shouldShow;
        row.dataset.pfBoostRowState = shouldShow ? "eligible" : "ineligible";
        const mediaItem = section.querySelector(
          `${selectors.mediaPanel} ${selectors.mediaItem}[data-block-index="${rows.indexOf(row)}"]`
        );
        if (mediaItem && mediaItem.hidden !== !shouldShow) mediaItem.hidden = !shouldShow;
      });
  };

  const togglePfScrollComplementaryOption = (option) => {
    const section = option?.closest?.(selectors.section);
    const variantId = option?.dataset?.variantId;
    if (!section || !variantId || option.disabled) return;

    const selectedIds = getPfScrollComplementaryVariantIds(section);
    const wasSelected = selectedIds.has(variantId);
    if (selectedIds.has(variantId)) {
      selectedIds.delete(variantId);
    } else {
      selectedIds.add(variantId);
    }
    debugPfScrollAddOns("option toggled", {
      sectionId: getPfScrollDebugSectionId(section),
      variantId,
      wasSelected,
      selectedIds: Array.from(selectedIds),
    });
    setPfScrollComplementaryVariantIds(section, selectedIds);
    syncPfScrollComplementarySelectorState(section);
  };

  const PF_SCROLL_RECS_MAX_ATTEMPTS = 6;
  const PF_SCROLL_RECS_RETRY_BASE_MS = 700;

  /**
   * Fetch + inject complementary recommendations ourselves so we control retries. Shopify's
   * recommendations API returns an empty result on a cache miss (common on the first request for a
   * product on the live storefront) and populates on a subsequent request a moment later. The
   * theme's native loadRecommendations fetches once and never retries, so it gives up empty. We
   * retry on empty until options arrive (or attempts are exhausted).
   */
  const fetchPfScrollComplementaryRecommendations = (recommendations, attempt = 1) => {
    const { url, productId, sectionId } = recommendations.dataset;
    const requestUrl = new URL(url, window.location.origin);
    const previewThemeId = getPfScrollPreviewThemeId();
    if (previewThemeId) requestUrl.searchParams.set("preview_theme_id", previewThemeId);
    recommendations.dataset.url = requestUrl.toString();
    requestUrl.searchParams.set("product_id", productId);
    requestUrl.searchParams.set("section_id", sectionId);
    const requestUrlString = requestUrl.toString();
    debugPfScrollAddOns("recs fetch start", {
      productId,
      attempt,
      requestUrl: requestUrlString,
    });

    fetch(requestUrlString)
      .then((response) => response.text())
      .then((text) => {
        if (!recommendations.isConnected) return;
        if (recommendations.querySelector(selectors.complementaryOption)) return;

        const html = document.createElement("div");
        html.innerHTML = text;
        const source =
          html.querySelector("[data-pf-scroll-complementary-selector]") ||
          html.querySelector("product-recommendations");
        const optionCount = source
          ? source.querySelectorAll(selectors.complementaryOption).length
          : 0;
        const inner = source ? source.innerHTML.trim() : "";
        debugPfScrollAddOns("recs fetch parsed", {
          productId,
          attempt,
          foundSource: Boolean(source),
          optionCount,
          innerLen: inner.length,
        });

        if (optionCount > 0) {
          recommendations.innerHTML = inner;
          recommendations.dataset.pfScrollComplementaryStatus = "ready";
          const section = recommendations.closest(selectors.section) || document;
          syncPfScrollComplementarySelectorState(section);
          syncPfScrollComplementaryRowVisibility(section);
          return;
        }

        if (attempt < PF_SCROLL_RECS_MAX_ATTEMPTS) {
          const delay = PF_SCROLL_RECS_RETRY_BASE_MS * attempt;
          debugPfScrollAddOns("recs fetch empty, retrying", { productId, attempt, delay });
          window.setTimeout(
            () => fetchPfScrollComplementaryRecommendations(recommendations, attempt + 1),
            delay
          );
        } else {
          recommendations.dataset.pfScrollComplementaryStatus = "empty";
          syncPfScrollComplementaryRowVisibility(
            recommendations.closest(selectors.section) || document
          );
          debugPfScrollAddOns("recs fetch gave up (still empty)", { productId, attempt });
        }
      })
      .catch((error) => {
        debugPfScrollAddOns("recs fetch error", {
          productId,
          attempt,
          message: error?.message || String(error),
        });
        if (attempt < PF_SCROLL_RECS_MAX_ATTEMPTS && recommendations.isConnected) {
          window.setTimeout(
            () => fetchPfScrollComplementaryRecommendations(recommendations, attempt + 1),
            PF_SCROLL_RECS_RETRY_BASE_MS * attempt
          );
        }
      });
  };

  /**
   * The complementary list is a native <product-recommendations> element that lazy-loads on
   * IntersectionObserver. Inside the pinned scroll section that observer may never fire (so it stays
   * empty in preview/live). We disable the native observer and load it ourselves with retry-on-empty.
   */
  const eagerLoadPfScrollComplementaryRecommendations = (section) => {
    const recommendationsList = section.querySelectorAll("[data-pf-scroll-complementary-selector]");
    debugPfScrollAddOns("eager load called", {
      sectionId: getPfScrollDebugSectionId(section),
      recommendationCount: recommendationsList.length,
    });

    recommendationsList.forEach((recommendations, index) => {
      if (recommendations.dataset.pfScrollEagerLoaded === "true") {
        recommendations.dataset.pfScrollComplementaryStatus = recommendations.querySelector(
          selectors.complementaryOption
        )
          ? "ready"
          : "empty";
        return;
      }
      if (recommendations.querySelector(selectors.complementaryOption)) {
        recommendations.dataset.pfScrollComplementaryStatus = "ready";
        return;
      }

      const { productId } = recommendations.dataset;
      if (!productId) {
        recommendations.dataset.pfScrollComplementaryStatus = "empty";
        syncPfScrollComplementaryRowVisibility(section);
        debugPfScrollAddOns("eager load skip: missing productId", { index });
        return;
      }

      recommendations.dataset.pfScrollEagerLoaded = "true";
      recommendations.dataset.pfScrollComplementaryStatus = "loading";
      recommendations.observer?.unobserve?.(recommendations);
      fetchPfScrollComplementaryRecommendations(recommendations);
    });
    syncPfScrollComplementaryRowVisibility(section);
  };

  const initPfScrollComplementarySelectors = (section, lifecycleSignal) => {
    debugPfScrollAddOns("init complementary selectors", {
      sectionId: getPfScrollDebugSectionId(section),
      isLive: isLivePfScrollSection(section),
      recommendationCount: section.querySelectorAll("[data-pf-scroll-complementary-selector]")
        .length,
      optionCount: section.querySelectorAll(selectors.complementaryOption).length,
      summaryCount: section.querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
        .length,
    });
    eagerLoadPfScrollComplementaryRecommendations(section);
    syncPfScrollComplementarySelectorState(section);
    syncPfScrollComplementaryRowVisibility(section);
    syncPfPilotBoostOfferRowVisibility(section);

    section.addEventListener(
      "click",
      (event) => {
        const boostDecline = event.target.closest("[data-pf-boost-offer-decline]");
        if (boostDecline && section.contains(boostDecline)) {
          event.preventDefault();
          const boostOffer = boostDecline.closest("[data-pf-pilot-boost-offer]");
          const boostOption = boostOffer?.querySelector(selectors.complementaryOption);
          if (boostOption?.classList.contains("is-selected")) {
            togglePfScrollComplementaryOption(boostOption);
          }
          boostDecline.classList.add("is-selected");
          boostDecline.setAttribute("aria-pressed", "true");
          return;
        }
        const boostRemove = event.target.closest("[data-pf-boost-offer-remove]");
        if (boostRemove && section.contains(boostRemove)) {
          event.preventDefault();
          const boostOption = boostRemove
            .closest("[data-pf-pilot-boost-offer]")
            ?.querySelector(selectors.complementaryOption);
          if (boostOption?.classList.contains("is-selected")) {
            togglePfScrollComplementaryOption(boostOption);
          }
          return;
        }
        const option = event.target.closest(selectors.complementaryOption);
        if (!option || !section.contains(option)) return;
        event.preventDefault();
        const boostDeclineControl = option
          .closest("[data-pf-pilot-boost-offer]")
          ?.querySelector("[data-pf-boost-offer-decline]");
        boostDeclineControl?.classList.remove("is-selected");
        boostDeclineControl?.setAttribute("aria-pressed", "false");
        togglePfScrollComplementaryOption(option);
      },
      { signal: lifecycleSignal }
    );

    let syncQueued = false;
    const queueSync = () => {
      if (syncQueued) return;
      syncQueued = true;
      window.requestAnimationFrame(() => {
        syncQueued = false;
        debugPfScrollAddOns("mutation observer resync", {
          sectionId: getPfScrollDebugSectionId(section),
          optionCount: section.querySelectorAll(selectors.complementaryOption).length,
          summaryCount: section.querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
            .length,
        });
        syncPfScrollComplementarySelectorState(section);
        syncPfScrollComplementaryRowVisibility(section);
        syncPfPilotBoostOfferRowVisibility(section);
      });
    };

    const observer = new MutationObserver(queueSync);
    observer.observe(section, { childList: true, subtree: true });
    lifecycleSignal?.addEventListener("abort", () => observer.disconnect(), { once: true });
  };

  const PF_SCROLL_NO_COVERAGE = "no_coverage";
  /**
   * The block renders one of two panel styles so a Shoplift template test can compare them from the
   * same code. "current" is the four-card row live since the SureBright v2 migration and must keep
   * behaving exactly like it; everything the redesign added is gated on "quick".
   */
  const getPfScrollWarrantyUiVersion = (mirror) =>
    mirror?.dataset?.pfScrollWarrantyUi === "quick" ? "quick" : "current";
  const isPfScrollWarrantyQuick = (mirror) => getPfScrollWarrantyUiVersion(mirror) === "quick";
  /**
   * Selection motion (the app's shield, ripple, date roll, "Protected through" line, price bounce
   * and haptics) only runs when the block's "Selection motion" setting renders this attribute.
   * With it off the current panel must render and behave exactly as before.
   */
  const isPfScrollWarrantyMotion = (mirror) => mirror?.dataset?.pfScrollWarrantyMotion === "on";
  const prefersPfScrollReducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

  /**
   * The selector must not render inside the Fuego app webview: nothing there runs SureBright's
   * sync engine, so a policy added from the app would never be registered. These are the same
   * host bridges assets/reusely-trade-in-app.js posts to.
   */
  const isPfScrollWarrantyAppHost = () =>
    Boolean(
      window.ReactNativeWebView || window.PlugReusely || window.webkit?.messageHandlers?.plugReusely
    );
  const pfScrollWarrantyQuoteStates = new WeakMap();
  const pfScrollExplicitWarrantyChoices = new WeakMap();
  /** Covered variant ID -> coverage years already in the cart, so the PDP opens on that term. */
  let pfScrollCartCoverageYears = new Map();
  let pfScrollCartCoverageRequest = 0;
  let pfScrollCartCoverageBound = false;

  const readPfScrollCartCoverage = (cart) => {
    const coverage = new Map();
    (Array.isArray(cart?.items) ? cart.items : []).forEach((item) => {
      const properties = Object.entries(item?.properties || {});
      const read = (key) =>
        String(properties.find(([name]) => name.toLowerCase() === key)?.[1] || "");
      const coveredVariantId = read("_variantid");
      if (!coveredVariantId || !read("_sbquoteitemid")) return;
      const years = read("term").match(/\b([1-3])\s*[- ]?years?\b/i)?.[1] || "";
      if (years) coverage.set(coveredVariantId, years);
    });
    return coverage;
  };

  const refreshPfScrollCartCoverage = () => {
    const request = ++pfScrollCartCoverageRequest;
    const root = window.Shopify?.routes?.root || "/";
    fetch(`${root.endsWith("/") ? root : `${root}/`}cart.js`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart || request !== pfScrollCartCoverageRequest) return;
        pfScrollCartCoverageYears = readPfScrollCartCoverage(cart);
        getLivePfScrollSections().forEach((section) => {
          const quote = pfScrollWarrantyQuoteStates.get(section)?.quote;
          if (quote) renderPfScrollWarrantySelector(section, quote);
        });
      })
      .catch(() => {
        // Without the cart the selector keeps its normal default; the add-to-cart guard still runs.
      });
  };

  const bindPfScrollCartCoverageOnce = () => {
    if (pfScrollCartCoverageBound) return;
    pfScrollCartCoverageBound = true;
    refreshPfScrollCartCoverage();
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshPfScrollCartCoverage);
    }
  };

  let pfChoiceHelperCartTrackingBound = false;
  const trackPfChoiceHelperAddToCart = (event) => {
    // publish() runs subscribers inline, so a failure here must never reach the cart flow.
    try {
      if (event?.source !== "product-form") return;
      const section = document.querySelector(selectors.section);
      if (!section) return;
      const form = section.querySelector("[data-pf-scroll-pilot-form] form");
      // The pilot's cartData merges in the whole cart, so read the added line from the form.
      const formData = form ? new FormData(form) : null;
      const shown = Array.from(section.querySelectorAll("[data-pf-choice-helper]"))
        .filter((helper) => helper.getClientRects().length > 0)
        .map((helper) => helper.getAttribute("data-pf-choice-helper"));
      const detail = {
        product_id: String(getPfScrollProductId(section) || ""),
        product_handle: window.location.pathname.split("/products/")[1]?.split("/")[0] || "",
        variant_id: String(event.productVariantId || formData?.get("id") || ""),
        quantity: Number.parseInt(formData?.get("quantity") || "1", 10) || 1,
        helpers_shown: Array.from(new Set(shown)).join(","),
        helpers_exposed: Array.from(pfChoiceHelpersExposedOnPage).join(","),
        helpers_opened: Array.from(pfChoiceHelpersOpenedOnPage).join(","),
        opened_any_helper: pfChoiceHelpersOpenedOnPage.size > 0,
        source: "pf_scroll",
        pdp_experience: "pf_scroll_pilot",
      };
      // Guided option steps only: whether the page opened with Condition already chosen.
      const conditionPreselected = window.PfScrollGuidedSteps?.conditionPreselected?.();
      if (typeof conditionPreselected === "boolean") {
        detail.condition_preselected = conditionPreselected;
      }
      // And whether every option (Color, Storage...) was already chosen.
      const optionsPreselected = window.PfScrollGuidedSteps?.optionsPreselected?.();
      if (typeof optionsPreselected === "boolean") {
        detail.options_preselected = optionsPreselected;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "pdp_add_to_cart", ...detail });
      window.posthog?.capture?.("pdp_add_to_cart", detail);
    } catch {
      // Tracking is best effort.
    }
  };

  const bindPfChoiceHelperCartTrackingOnce = () => {
    if (pfChoiceHelperCartTrackingBound) return;
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;
    pfChoiceHelperCartTrackingBound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, trackPfChoiceHelperAddToCart);
  };

  const getPfScrollWarrantyContext = (section) => {
    const mirror = section.querySelector(selectors.warrantySelector);
    const productInfo = getPfScrollProductInfo(section);
    const productId = String(getPfScrollProductId(section) || "");
    const variantId = String(
      getPfScrollSelectedVariant(productInfo)?.id || productInfo?.dataset?.pfSelectedVariantId || ""
    );
    const country = mirror?.dataset.pfSbCountry || "US";
    const language = mirror?.dataset.pfSbLocale || "en";
    const productHandle =
      String(productInfo?.dataset?.url || "").match(/\/products\/([^/?#]+)/)?.[1] || "";
    return {
      country,
      locale: language.includes("-") ? language : `${language}-${country}`,
      productHandle,
      productId,
      shop: mirror?.dataset.pfSbShop || "",
      variantId,
    };
  };

  const clearPfScrollExplicitWarrantyChoice = (section) => {
    pfScrollExplicitWarrantyChoices.delete(section);
    delete section.dataset.pfScrollWarrantyChoice;
    delete section.dataset.pfScrollWarrantyChoiceProductId;
    delete section.dataset.pfScrollWarrantyChoiceVariantId;
    delete section.dataset.pfScrollWarrantyChoiceSource;
  };

  const getPfScrollExplicitWarrantyChoice = (section) => {
    const choice = pfScrollExplicitWarrantyChoices.get(section);
    if (!choice) return null;
    const productId = String(getPfScrollProductId(section) || "");
    if (choice.productId === productId) return choice;

    clearPfScrollExplicitWarrantyChoice(section);
    return null;
  };

  const setPfScrollExplicitWarrantyChoice = (
    section,
    { quoteItemId = "", years = "", variantId = "" },
    choiceSource = "customer"
  ) => {
    const productId = String(getPfScrollProductId(section) || "");
    const isNoCoverage = !quoteItemId;
    const choice = { choiceSource, isNoCoverage, productId, quoteItemId, variantId, years };
    pfScrollExplicitWarrantyChoices.set(section, choice);
    section.dataset.pfScrollWarrantyChoice = isNoCoverage ? PF_SCROLL_NO_COVERAGE : quoteItemId;
    section.dataset.pfScrollWarrantyChoiceProductId = productId;
    section.dataset.pfScrollWarrantyChoiceVariantId = variantId;
    section.dataset.pfScrollWarrantyChoiceSource = choiceSource;
    return choice;
  };

  /**
   * Keeps the customer's choice bound to the quote on screen. A paid choice follows the same term
   * when the variant changes; anything that cannot be rebound falls back to the No Coverage default.
   * Until the customer picks, a variant already covered in the cart opens on that same term.
   */
  const resolvePfScrollWarrantyChoice = (section, quote) => {
    let choice = getPfScrollExplicitWarrantyChoice(section);
    const variantId = String(quote.variantId);
    const isAutomatic =
      !choice || choice.choiceSource === "default" || choice.choiceSource === "cart";
    if (isAutomatic) {
      const cartYears = pfScrollCartCoverageYears.get(variantId);
      const cartPlan = cartYears && quote.plans.find((plan) => plan.years === cartYears);
      if (cartPlan) {
        if (choice?.quoteItemId === cartPlan.quoteItemId) return choice;
        return setPfScrollExplicitWarrantyChoice(
          section,
          { quoteItemId: cartPlan.quoteItemId, variantId, years: cartPlan.years },
          "cart"
        );
      }
      if (choice?.choiceSource === "cart") choice = null;
    }
    if (choice?.isNoCoverage) {
      if (choice.variantId !== variantId) {
        return setPfScrollExplicitWarrantyChoice(section, { variantId }, choice.choiceSource);
      }
      return choice;
    }
    if (choice) {
      if (quote.plans.some((plan) => plan.quoteItemId === choice.quoteItemId)) return choice;
      const sameTerm = quote.plans.find((plan) => plan.years === choice.years);
      if (sameTerm) {
        return setPfScrollExplicitWarrantyChoice(
          section,
          { quoteItemId: sameTerm.quoteItemId, variantId, years: sameTerm.years },
          "rebind"
        );
      }
    }
    const mirror = getPfScrollWarrantyMirror(section);
    if (
      isPfScrollWarrantyQuick(mirror) &&
      getPfScrollWarrantyDefaultSelection(mirror) === "two_year"
    ) {
      const preferred = getPfScrollWarrantyPreferredPlan(quote);
      if (preferred) {
        return setPfScrollExplicitWarrantyChoice(
          section,
          { quoteItemId: preferred.quoteItemId, variantId, years: preferred.years },
          "default"
        );
      }
    }
    return setPfScrollExplicitWarrantyChoice(section, { variantId }, "default");
  };

  const syncPfScrollWarrantyItemState = (item, selectedIndex, choice) => {
    const isSelected = Number(item.dataset.pfScrollWarrantyIndex) === selectedIndex;
    const isDefault = isSelected && choice?.choiceSource === "default";
    item.classList.toggle("is-selected", isSelected);
    const radio = item.matches('[role="radio"]') ? item : item.querySelector('[role="radio"]');
    if (!radio) item.setAttribute("aria-pressed", isSelected ? "true" : "false");
    item.toggleAttribute("data-pf-scroll-warranty-default", isDefault);
    item.toggleAttribute("data-pf-scroll-warranty-explicit", isSelected && !isDefault);
    if (!radio) return;
    radio.setAttribute("aria-checked", isSelected ? "true" : "false");
    // Roving tabindex: exactly one card is always selected, so exactly one stays tabbable.
    radio.tabIndex = isSelected ? 0 : -1;
  };

  const getPfScrollWarrantyTermYears = (title) => {
    const match = String(title || "").match(/(\d+(?:[.,]\d+)?)\s*[-–]?\s*(?:year|yr)/i);
    if (!match) return null;
    const years = Number(match[1].replace(",", "."));
    return Number.isFinite(years) && years > 0 ? years : null;
  };

  const parsePfScrollWarrantyMoney = (price) => {
    const value = String(price || "").trim();
    const match = value.match(/\d[\d\s.,]*\d|\d/);
    if (!match) return null;

    const numericText = match[0].replace(/\s/g, "");
    const lastComma = numericText.lastIndexOf(",");
    const lastDot = numericText.lastIndexOf(".");
    let decimalSeparator = "";

    if (lastComma !== -1 && lastDot !== -1) {
      decimalSeparator = lastComma > lastDot ? "," : ".";
    } else {
      const separatorIndex = Math.max(lastComma, lastDot);
      if (separatorIndex !== -1) {
        const digitsAfterSeparator = numericText.length - separatorIndex - 1;
        if (digitsAfterSeparator > 0 && digitsAfterSeparator <= 2) {
          decimalSeparator = numericText[separatorIndex];
        }
      }
    }

    let normalized = numericText.replace(/[.,]/g, "");
    if (decimalSeparator) {
      const decimalIndex = numericText.lastIndexOf(decimalSeparator);
      const whole = numericText.slice(0, decimalIndex).replace(/[.,]/g, "");
      const fraction = numericText.slice(decimalIndex + 1).replace(/[.,]/g, "");
      normalized = `${whole}.${fraction}`;
    }

    const amount = Number(normalized);
    if (!Number.isFinite(amount)) return null;

    return {
      amount,
      decimalSeparator: decimalSeparator || ".",
      prefix: value.slice(0, match.index),
      suffix: value.slice(match.index + match[0].length),
    };
  };

  const getPfScrollWarrantyMonthlyPrice = (price, title) => {
    const money = parsePfScrollWarrantyMoney(price);
    const years = getPfScrollWarrantyTermYears(title);
    if (!money || !years) return "";

    const monthlyAmount = (money.amount / (years * 12)).toFixed(2);
    const localizedAmount = monthlyAmount.replace(".", money.decimalSeparator);
    return `${money.prefix}${localizedAmount}${money.suffix}`.trim();
  };

  /**
   * pf-scroll-pilot-product-form.js reads the one `.pf-scroll-warranty-selector__price` inside the
   * selected card as price evidence before adding to cart. Both card styles render it through here.
   */
  const renderPfScrollWarrantyPrice = (price) =>
    `<span class="pf-scroll-warranty-selector__price">${escapeHTML(price)}</span>`;

  const PF_SCROLL_WARRANTY_DEFAULT_SELECTIONS = ["none", "two_year"];
  const PF_SCROLL_WARRANTY_PRICE_FRAMINGS = ["one_time", "one_time_plus_monthly"];

  /**
   * Reads one of the two review settings. On a preview theme the block opts into URL overrides so
   * both answers can be compared without a theme-editor round trip; production leaves that off.
   */
  const readPfScrollWarrantyOption = (mirror, datasetKey, param, allowed) => {
    let value = String(mirror?.dataset?.[datasetKey] || "").trim();
    if (mirror?.hasAttribute?.("data-pf-scroll-warranty-preview-overrides")) {
      const override = new URL(window.location.href).searchParams.get(param);
      if (override) value = String(override).trim();
    }
    return allowed.includes(value) ? value : allowed[0];
  };

  const getPfScrollWarrantyDefaultSelection = (mirror) =>
    readPfScrollWarrantyOption(
      mirror,
      "pfScrollWarrantyDefaultSelection",
      "pf_warranty_preselect",
      PF_SCROLL_WARRANTY_DEFAULT_SELECTIONS
    );

  const getPfScrollWarrantyPriceFraming = (mirror) =>
    readPfScrollWarrantyOption(
      mirror,
      "pfScrollWarrantyPriceFraming",
      "pf_warranty_framing",
      PF_SCROLL_WARRANTY_PRICE_FRAMINGS
    );

  const getPfScrollWarrantyMirror = (section) =>
    section?.querySelector?.(selectors.warrantySelector) || null;

  /** SureBright's coverageType B plans cover breakdowns only, so the accident copy must not show. */
  const isPfScrollWarrantyBreakdownOnly = (quote) =>
    String(quote?.coverageType || "")
      .trim()
      .toUpperCase() === "B";

  const getPfScrollWarrantyPreferredPlan = (quote) => {
    const plans = Array.isArray(quote?.plans) ? quote.plans : [];
    return (
      plans.find((plan) => plan.ribbon) ||
      plans.find((plan) => plan.isDefault) ||
      plans.find((plan) => String(plan.years) === "2") ||
      plans[plans.length - 1] ||
      null
    );
  };

  const formatPfScrollWarrantyAmount = (amount) => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return "";
    return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;
  };

  const fillPfScrollWarrantyTemplate = (template, values) =>
    Object.entries(values).reduce(
      (text, [token, value]) => text.split(token).join(value),
      String(template || "")
    );

  const getPfScrollWarrantyTermLabel = (mirror, years) => {
    const label =
      Number(years) === 1
        ? mirror?.dataset?.pfScrollWarrantyYear || "Year"
        : mirror?.dataset?.pfScrollWarrantyYears || "Years";
    return `${years} ${label}`;
  };

  const getPfScrollWarrantyCoverageDate = (years) => {
    const numeric = Number(years);
    if (!Number.isFinite(numeric) || numeric <= 0) return "";
    const coverageEnd = new Date();
    coverageEnd.setMonth(coverageEnd.getMonth() + Math.round(numeric * 12));
    const locale = document.documentElement.lang || "en";
    return new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format(coverageEnd);
  };

  const getPfScrollWarrantyDisplayTitle = (mirror, title) => {
    const years = getPfScrollWarrantyTermYears(title);
    if (!years) return title;

    const numericTerm = String(title).match(/\d+(?:[.,]\d+)?/)?.[0] || String(years);
    const yearLabel =
      years === 1
        ? mirror.dataset.pfScrollWarrantyYear || "Year"
        : mirror.dataset.pfScrollWarrantyYears || "Years";
    const term = `${numericTerm} ${yearLabel}`;
    const titleTemplate = mirror.dataset.pfScrollWarrantyPlanTitle || "__TERM__";
    return titleTemplate.replace("__TERM__", term);
  };

  const getPfScrollWarrantyPlanDescription = (mirror, title) => {
    const fallback = (mirror.dataset.pfScrollWarrantyPlanDescriptionFallback || "").replace(
      "__AMP__",
      "&"
    );
    const years = getPfScrollWarrantyTermYears(title);
    if (!years) return fallback;

    const coverageEnd = new Date();
    coverageEnd.setMonth(coverageEnd.getMonth() + Math.round(years * 12));
    const locale = document.documentElement.lang || "en";
    const coverageDate = new Intl.DateTimeFormat(locale, {
      month: "short",
      year: "numeric",
    }).format(coverageEnd);
    const descriptionTemplate = (mirror.dataset.pfScrollWarrantyPlanDescription || "").replace(
      "__AMP__",
      "&"
    );
    return descriptionTemplate ? descriptionTemplate.replace("__DATE__", coverageDate) : fallback;
  };

  /**
   * Selection motion wraps the coverage date in its own span so a tap can roll it month by month.
   * The text stays exactly what getPfScrollWarrantyPlanDescription renders.
   */
  const renderPfScrollWarrantyPlanDescription = (mirror, title, planDescription) => {
    const template = (mirror.dataset.pfScrollWarrantyPlanDescription || "").replace("__AMP__", "&");
    const years = getPfScrollWarrantyTermYears(title);
    const dateIndex = template.indexOf("__DATE__");
    if (!isPfScrollWarrantyMotion(mirror) || !years || dateIndex === -1) {
      return escapeHTML(planDescription);
    }
    return `${escapeHTML(
      template.slice(0, dateIndex)
    )}<span class="pf-scroll-warranty-selector__date" data-pf-scroll-warranty-date-months="${Math.round(
      years * 12
    )}">${escapeHTML(getPfScrollWarrantyCoverageDate(years))}</span>${escapeHTML(
      template.slice(dateIndex + "__DATE__".length)
    )}`;
  };

  /**
   * The shield that replaces the radio on the selected paid card (the app's delivered SVG). It is
   * fully drawn at rest; the tap only adds a pop and a check draw on top. Each card gets its own
   * gradient id so cards and mirrors never share one.
   */
  let pfScrollWarrantyShieldCount = 0;
  const renderPfScrollWarrantyShield = () => {
    pfScrollWarrantyShieldCount += 1;
    const id = `pf-sb-shield-${pfScrollWarrantyShieldCount}`;
    return `<svg class="pf-scroll-warranty-selector__shield" viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true" focusable="false"><defs><linearGradient id="${id}" x1="3" y1="12" x2="21" y2="12" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#4ABFA7"/><stop offset="0.5" stop-color="#87D8F2"/><stop offset="1" stop-color="#E5C94E"/></linearGradient></defs><path d="M12 1.5l9 3.4v6.4c0 5.6-3.9 10-9 11.9-5.1-1.9-9-6.3-9-11.9V4.9z" fill="url(#${id})"/><path class="pf-scroll-warranty-selector__shield-check" d="M7.6 12.2l3 3 5.8-6.4" stroke="#112D2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  };

  /** Current style: one card per term plus the decline card, exactly as production renders it. */
  const buildPfScrollWarrantyCurrentCard = (mirror, plan, index) => {
    const isNoCoverage = !plan;
    const title = isNoCoverage ? "No Coverage" : window.PfSureBrightQuotes.formatTerm(plan.years);
    const price = isNoCoverage ? "" : plan.priceText;
    const monthlyPrice = getPfScrollWarrantyMonthlyPrice(price, title);
    const ribbonText =
      !isNoCoverage && plan.ribbon
        ? mirror.dataset.pfScrollWarrantyMostPopular || plan.ribbonText
        : "";
    const declineText = mirror.dataset.pfScrollWarrantyDecline || title;
    const perMonthText = mirror.dataset.pfScrollWarrantyPerMonth || "";
    const displayTitle = getPfScrollWarrantyDisplayTitle(mirror, title);
    const planDescription = getPfScrollWarrantyPlanDescription(mirror, title);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "pf-scroll-warranty-selector__item";
    button.dataset.pfScrollWarrantyIndex = String(index);
    button.dataset.pfScrollWarrantyChoiceValue = isNoCoverage
      ? PF_SCROLL_NO_COVERAGE
      : plan.quoteItemId;
    if (!isNoCoverage) button.dataset.pfScrollWarrantyPriceCents = String(plan.priceCents);
    button.setAttribute("aria-pressed", "false");
    button.classList.toggle("pf-scroll-warranty-selector__item--decline", isNoCoverage);

    if (isNoCoverage) {
      button.innerHTML = `
      <span class="pf-scroll-warranty-selector__radio" aria-hidden="true"></span>
      <span class="pf-scroll-warranty-selector__content">
        <span class="pf-scroll-warranty-selector__title pf-scroll-warranty-selector__decline-text">${escapeHTML(
          declineText
        )}</span>
      </span>
    `;
      return button;
    }

    button.innerHTML = `
    ${
      ribbonText
        ? `<span class="pf-scroll-warranty-selector__ribbon">${escapeHTML(ribbonText)}</span>`
        : ""
    }
    <span class="pf-scroll-warranty-selector__radio" aria-hidden="true">${
      isPfScrollWarrantyMotion(mirror) ? renderPfScrollWarrantyShield() : ""
    }</span>
    <span class="pf-scroll-warranty-selector__content">
      <span class="pf-scroll-warranty-selector__title">${escapeHTML(displayTitle)}</span>
      ${
        planDescription
          ? `<span class="pf-scroll-warranty-selector__subtitle">${renderPfScrollWarrantyPlanDescription(
              mirror,
              title,
              planDescription
            )}</span>`
          : ""
      }
    </span>
    <span class="pf-scroll-warranty-selector__price-row">
      ${renderPfScrollWarrantyPrice(price)}
      ${
        monthlyPrice && perMonthText
          ? `<span class="pf-scroll-warranty-selector__monthly">${escapeHTML(
              monthlyPrice
            )}${escapeHTML(perMonthText)}</span>`
          : ""
      }
    </span>
  `;
    return button;
  };

  /**
   * The paid card. It owns the choice value and the single parseable price, so the add-to-cart
   * price-evidence guard keeps reading exactly one `.pf-scroll-warranty-selector__price` on the
   * selected card. Term pills price themselves with a separate class on purpose.
   */
  const buildPfScrollWarrantyPlanCard = (mirror, plan, index, context) => {
    const { quote, framing, breakdownOnly } = context;
    const card = document.createElement("div");
    card.className = "pf-scroll-warranty-selector__item pf-scroll-warranty-selector__item--plan";
    card.dataset.pfScrollWarrantyIndex = String(index);
    card.dataset.pfScrollWarrantyChoiceValue = plan.quoteItemId;
    card.dataset.pfScrollWarrantyPriceCents = String(plan.priceCents);

    const coverageDate = getPfScrollWarrantyCoverageDate(plan.years);
    const title =
      (breakdownOnly
        ? mirror.dataset.pfScrollWarrantyPlanCardTitleBreakdown
        : mirror.dataset.pfScrollWarrantyPlanCardTitle) || "";
    const subtitleTemplate = breakdownOnly
      ? mirror.dataset.pfScrollWarrantyPlanSubtitleBreakdown
      : mirror.dataset.pfScrollWarrantyPlanSubtitle;
    const subtitle =
      coverageDate && subtitleTemplate
        ? fillPfScrollWarrantyTemplate(subtitleTemplate, { __DATE__: coverageDate })
        : mirror.dataset.pfScrollWarrantyPlanSubtitleFallback || "";
    const priceNote = mirror.dataset.pfScrollWarrantyPriceNote || "";
    const perMonthText = mirror.dataset.pfScrollWarrantyPerMonth || "";
    const monthlyPrice =
      framing === "one_time_plus_monthly"
        ? getPfScrollWarrantyMonthlyPrice(
            plan.priceText,
            window.PfSureBrightQuotes.formatTerm(plan.years)
          )
        : "";
    const pills = quote.plans
      .map((candidate) => {
        const isActive = candidate.quoteItemId === plan.quoteItemId;
        const ribbonText = candidate.ribbon
          ? mirror.dataset.pfScrollWarrantyMostPopular || candidate.ribbonText || ""
          : "";
        return `
        <button
          type="button"
          class="pf-scroll-warranty-selector__term${isActive ? " is-active" : ""}"
          data-pf-scroll-warranty-choice-value="${escapeHTML(candidate.quoteItemId)}"
          data-pf-scroll-warranty-term="${escapeHTML(String(candidate.years))}"
          aria-pressed="${isActive ? "true" : "false"}"
        >
          ${
            ribbonText
              ? `<span class="pf-scroll-warranty-selector__ribbon">${escapeHTML(ribbonText)}</span>`
              : ""
          }
          <span class="pf-scroll-warranty-selector__term-label">${escapeHTML(
            getPfScrollWarrantyTermLabel(mirror, candidate.years)
          )}</span>
          <span class="pf-scroll-warranty-selector__term-price">${escapeHTML(
            candidate.priceText
          )}</span>
        </button>`;
      })
      .join("");

    card.innerHTML = `
      <button
        type="button"
        class="pf-scroll-warranty-selector__choose"
        role="radio"
        aria-checked="false"
        tabindex="-1"
      >
        <span class="pf-scroll-warranty-selector__radio" aria-hidden="true"></span>
        <span class="pf-scroll-warranty-selector__content">
          <span class="pf-scroll-warranty-selector__title">${escapeHTML(title)}</span>
          ${
            subtitle
              ? `<span class="pf-scroll-warranty-selector__subtitle">${escapeHTML(subtitle)}</span>`
              : ""
          }
        </span>
        <span class="pf-scroll-warranty-selector__price-row">
          ${renderPfScrollWarrantyPrice(plan.priceText)}
          ${
            priceNote
              ? `<span class="pf-scroll-warranty-selector__price-note">${escapeHTML(
                  priceNote
                )}</span>`
              : ""
          }
          ${
            monthlyPrice && perMonthText
              ? `<span class="pf-scroll-warranty-selector__monthly">${escapeHTML(
                  monthlyPrice
                )}${escapeHTML(perMonthText)}</span>`
              : ""
          }
        </span>
      </button>
      <div
        class="pf-scroll-warranty-selector__terms"
        role="group"
        aria-label="${escapeHTML(mirror.dataset.pfScrollWarrantyTermsLabel || "")}"
      >${pills}</div>
    `;
    return card;
  };

  const buildPfScrollWarrantyDeclineCard = (mirror, index, context) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "pf-scroll-warranty-selector__item pf-scroll-warranty-selector__item--decline";
    button.dataset.pfScrollWarrantyIndex = String(index);
    button.dataset.pfScrollWarrantyChoiceValue = PF_SCROLL_NO_COVERAGE;
    button.setAttribute("role", "radio");
    button.setAttribute("aria-checked", "false");
    button.tabIndex = -1;
    const title =
      (context.breakdownOnly
        ? mirror.dataset.pfScrollWarrantyDeclineBreakdown
        : mirror.dataset.pfScrollWarrantyDecline) || "No accident protection";
    button.innerHTML = `
      <span class="pf-scroll-warranty-selector__radio" aria-hidden="true"></span>
      <span class="pf-scroll-warranty-selector__content">
        <span class="pf-scroll-warranty-selector__title pf-scroll-warranty-selector__decline-text">${escapeHTML(
          title
        )}</span>
      </span>
    `;
    return button;
  };

  const buildPfScrollWarrantyExperimentCard = (mirror, plan, index, context = {}) =>
    plan
      ? buildPfScrollWarrantyPlanCard(mirror, plan, index, context)
      : buildPfScrollWarrantyDeclineCard(mirror, index, context);

  const renderPfScrollWarrantySkeleton = (mirror, list) => {
    const label = mirror.dataset.pfScrollWarrantyLoading || "";
    list.innerHTML = `
      <span class="pf-scroll-warranty-selector__skeleton" aria-hidden="true"></span>
      <span class="pf-scroll-warranty-selector__skeleton pf-scroll-warranty-selector__skeleton--short"></span>
    `;
    list.setAttribute("aria-busy", "true");
    if (!label) return;
    // Remember the group's real name so the loading label cannot outlive the skeleton.
    if (!list.dataset.pfScrollWarrantyListLabel) {
      list.dataset.pfScrollWarrantyListLabel = list.getAttribute("aria-label") || "";
    }
    list.setAttribute("aria-label", label);
  };

  /**
   * While the quote is pending we do not yet know whether it covers accidents or only breakdowns,
   * so the claims stay off screen and only the heading sits above the skeleton.
   */
  const syncPfScrollWarrantyPendingCopy = (mirror, pending) => {
    const root = mirror.closest(".pf-scroll-warranty-quick") || mirror;
    [
      mirror.querySelector("[data-pf-scroll-warranty-lede-text]"),
      root.querySelector(".pf-scroll-warranty-selector__foot"),
    ].forEach((node) => {
      if (node) node.hidden = pending;
    });
  };

  const clearPfScrollWarrantySkeleton = (list) => {
    list.removeAttribute("aria-busy");
    const label = list.dataset.pfScrollWarrantyListLabel;
    if (label === undefined) return;
    if (label) list.setAttribute("aria-label", label);
    else list.removeAttribute("aria-label");
    delete list.dataset.pfScrollWarrantyListLabel;
  };

  const syncPfScrollWarrantyExperimentInfo = (mirror, quote, hasPaidPlans) => {
    const info = mirror.querySelector("[data-pf-scroll-warranty-info]");
    if (info) info.hidden = !hasPaidPlans;
    const root = mirror.closest(".pf-scroll-warranty-quick") || mirror;
    root.querySelectorAll("[data-pf-scroll-warranty-learn-more]").forEach((button) => {
      button.hidden = !quote?.learnMoreUrl;
    });
  };

  /**
   * Keeps the static panel copy honest for the plan type on screen and mirrors the choice into the
   * left media panel, the buy-box summary line and the compare drawer.
   */
  const syncPfScrollWarrantyPanelCopy = (section, mirror, quote, activePlan, choice) => {
    const breakdownOnly = isPfScrollWarrantyBreakdownOnly(quote);
    const root = mirror.closest(".pf-scroll-warranty-quick") || mirror;

    const heading = mirror.querySelector("[data-pf-scroll-warranty-heading-text]");
    if (heading) {
      heading.textContent = breakdownOnly
        ? mirror.dataset.pfScrollWarrantyHeadingBreakdown || heading.textContent
        : mirror.dataset.pfScrollWarrantyHeading || heading.textContent;
    }
    const lede = mirror.querySelector("[data-pf-scroll-warranty-lede-text]");
    if (lede) {
      lede.textContent = breakdownOnly
        ? mirror.dataset.pfScrollWarrantyLedeBreakdown || lede.textContent
        : mirror.dataset.pfScrollWarrantyLede || lede.textContent;
    }
    root.querySelectorAll('[data-pf-scroll-warranty-cover="accident"]').forEach((node) => {
      node.hidden = breakdownOnly;
    });
    const planColumn = root.querySelector("[data-pf-scroll-warranty-plan-column]");
    if (planColumn) {
      const columnTitle = planColumn.firstChild;
      const label = breakdownOnly
        ? mirror.dataset.pfScrollWarrantyProtectionColumnBreakdown
        : mirror.dataset.pfScrollWarrantyProtectionColumn;
      if (columnTitle && label) columnTitle.textContent = `${label} `;
    }

    const selectedPlan =
      choice && !choice.isNoCoverage
        ? quote.plans.find((plan) => plan.quoteItemId === choice.quoteItemId) || null
        : null;

    const deductible = formatPfScrollWarrantyAmount(activePlan?.deductible);
    const serviceFee = root.querySelector("[data-pf-scroll-warranty-service-fee]");
    if (serviceFee && deductible && mirror.dataset.pfScrollWarrantyServiceFeeBody) {
      serviceFee.textContent = fillPfScrollWarrantyTemplate(
        mirror.dataset.pfScrollWarrantyServiceFeeBody,
        { __AMOUNT__: deductible }
      );
    }

    syncPfScrollWarrantySummaryLine(section, mirror, selectedPlan);
    syncPfScrollWarrantyCoverageCard(section, mirror, selectedPlan, breakdownOnly);
  };

  /**
   * Bridges the scroll-row decision to the buy box. The complementary summary list is cached and
   * keyed on accessory variant ids, so protection gets its own line beside that list rather than
   * being pushed through it.
   */
  const ensurePfScrollWarrantySummaryLines = (section) => {
    section
      .querySelectorAll("[data-pf-scroll-selected-complementary-summary]")
      .forEach((summary) => {
        if (summary.querySelector("[data-pf-scroll-warranty-summary-line]")) return;
        const line = document.createElement("div");
        line.className = "pf-scroll-selected-complementary-products__protection";
        line.setAttribute("data-pf-scroll-warranty-summary-line", "");
        line.hidden = true;
        summary.appendChild(line);
      });
  };

  const syncPfScrollWarrantySummaryLine = (section, mirror, selectedPlan) => {
    ensurePfScrollWarrantySummaryLines(section);
    section.querySelectorAll("[data-pf-scroll-warranty-summary-line]").forEach((line) => {
      if (!selectedPlan) {
        line.innerHTML = "";
        line.hidden = true;
        return;
      }
      const title = fillPfScrollWarrantyTemplate(
        mirror.dataset.pfScrollWarrantySummaryTitle || "",
        { __TERM__: getPfScrollWarrantyTermLabel(mirror, selectedPlan.years) }
      );
      line.innerHTML = `
        <span class="pf-scroll-selected-complementary-products__protection-title">${escapeHTML(
          title
        )}</span>
        <span class="pf-scroll-selected-complementary-products__protection-price">${escapeHTML(
          selectedPlan.priceText
        )}</span>
      `;
      line.hidden = false;
    });
  };

  const getPfScrollWarrantyMediaItem = (section) => {
    const rows = Array.from(section.querySelectorAll(selectors.row));
    const index = rows.findIndex((row) => row.querySelector(selectors.warrantySelector));
    if (index === -1) return null;
    return section.querySelector(
      `${selectors.mediaPanel} ${selectors.mediaItem}[data-block-index="${index}"]`
    );
  };

  /**
   * The warranty row's left media panel is a static shield image in both product templates, and
   * templates/product.json is frozen by the pilot isolation gate, so the coverage mirror is
   * appended beneath that image rather than shipped as its own block.
   */
  const ensurePfScrollWarrantyCoverageCard = (section, mirror) => {
    const mediaItem = getPfScrollWarrantyMediaItem(section);
    if (!mediaItem) return null;
    const existing = mediaItem.querySelector("[data-pf-scroll-warranty-coverage-card]");
    if (existing) return existing;
    const card = document.createElement("div");
    card.className = "pf-scroll-warranty-coverage-card pf-scroll-warranty-coverage-card--off";
    card.setAttribute("data-pf-scroll-warranty-coverage-card", "");
    card.innerHTML = `
      <div class="pf-scroll-warranty-coverage-card__head">
        <span class="pf-scroll-warranty-coverage-card__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3 5 6v5c0 4.5 3 8.3 7 10 4-1.7 7-5.5 7-10V6Z"></path>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
        </span>
        <span class="pf-scroll-warranty-coverage-card__head-text">
          <strong data-pf-scroll-warranty-coverage-title></strong>
          <span data-pf-scroll-warranty-coverage-subtitle></span>
        </span>
      </div>
      <ul class="pf-scroll-warranty-coverage-card__list">
        <li data-pf-scroll-warranty-coverage-line="included">
          <span class="pf-scroll-warranty-coverage-card__mark" aria-hidden="true">&#10003;</span>
          <span data-pf-scroll-warranty-coverage-label="defects"></span>
        </li>
        <li data-pf-scroll-warranty-coverage-line="accident">
          <span class="pf-scroll-warranty-coverage-card__mark" aria-hidden="true">&#10005;</span>
          <span data-pf-scroll-warranty-coverage-label="drops"></span>
        </li>
        <li data-pf-scroll-warranty-coverage-line="accident">
          <span class="pf-scroll-warranty-coverage-card__mark" aria-hidden="true">&#10005;</span>
          <span data-pf-scroll-warranty-coverage-label="spills"></span>
        </li>
      </ul>
    `;
    const labels = {
      defects: mirror.dataset.pfScrollWarrantyCoverDefects || "",
      drops: mirror.dataset.pfScrollWarrantyCoverDrops || "",
      spills: mirror.dataset.pfScrollWarrantyCoverSpills || "",
    };
    Object.entries(labels).forEach(([key, text]) => {
      const node = card.querySelector(`[data-pf-scroll-warranty-coverage-label="${key}"]`);
      if (node) node.textContent = text;
    });
    mediaItem.appendChild(card);
    return card;
  };

  const syncPfScrollWarrantyCoverageCard = (section, mirror, selectedPlan, breakdownOnly) => {
    ensurePfScrollWarrantyCoverageCard(section, mirror);
    section.querySelectorAll("[data-pf-scroll-warranty-coverage-card]").forEach((card) => {
      card.classList.toggle("pf-scroll-warranty-coverage-card--off", !selectedPlan);
      const title = card.querySelector("[data-pf-scroll-warranty-coverage-title]");
      if (title) {
        const added = breakdownOnly
          ? mirror.dataset.pfScrollWarrantyMirrorAddedBreakdown
          : mirror.dataset.pfScrollWarrantyMirrorAdded;
        const notAdded = breakdownOnly
          ? mirror.dataset.pfScrollWarrantyMirrorNotAddedBreakdown
          : mirror.dataset.pfScrollWarrantyMirrorNotAdded;
        title.textContent = (selectedPlan ? added : notAdded) || title.textContent;
      }
      const subtitle = card.querySelector("[data-pf-scroll-warranty-coverage-subtitle]");
      if (subtitle) {
        const coverageDate = selectedPlan
          ? getPfScrollWarrantyCoverageDate(selectedPlan.years)
          : "";
        subtitle.textContent =
          selectedPlan && coverageDate && mirror.dataset.pfScrollWarrantyMirrorThrough
            ? fillPfScrollWarrantyTemplate(mirror.dataset.pfScrollWarrantyMirrorThrough, {
                __DATE__: coverageDate,
              })
            : mirror.dataset.pfScrollWarrantyMirrorPending || "";
      }
      card
        .querySelectorAll('[data-pf-scroll-warranty-coverage-line="accident"]')
        .forEach((line) => {
          line.hidden = breakdownOnly;
          line.classList.toggle("is-covered", Boolean(selectedPlan));
          const mark = line.querySelector(".pf-scroll-warranty-coverage-card__mark");
          if (mark) mark.innerHTML = selectedPlan ? "&#10003;" : "&#10005;";
        });
    });
  };

  const shouldSuppressPfScrollWarranty = (section) => {
    const productType = normalizePfScrollOptionName(
      section?.querySelector?.("[data-pf-product-type]")?.dataset?.pfProductType
    );
    return productType === "product warranty" || productType === "service plan";
  };

  const syncPfScrollWarrantyRowVisibility = (section, canReveal, suppressWarranty) => {
    if (!section?.querySelectorAll) return;
    const shouldShow = canReveal === true && suppressWarranty !== true;
    const hiddenState = suppressWarranty ? "suppressed-product-type" : "awaiting-offerings";
    const rows = Array.from(section.querySelectorAll(selectors.row));
    rows
      .filter((row) => row.querySelector(selectors.warrantySelector))
      .forEach((row) => {
        row.hidden = !shouldShow;
        row.dataset.pfWarrantyRowState = shouldShow ? "eligible" : hiddenState;
        const mediaItem = section.querySelector(
          `${selectors.mediaPanel} ${selectors.mediaItem}[data-block-index="${rows.indexOf(row)}"]`
        );
        if (mediaItem) mediaItem.hidden = !shouldShow;
      });
  };

  /**
   * Selection motion runtime. Effects only ever start from a trusted tap on a card: the click
   * handler marks the mirror it came from, and the render that follows plays the effects. Loads,
   * variant rebinds, cart syncs and list rebuilds render the finished state with no motion.
   */
  let pfScrollWarrantyMotionTap = null;
  let pfScrollWarrantyPaidHapticDone = false;
  const pfScrollWarrantyDateRolls = new WeakMap();

  // Android Chrome only; iOS Safari has no Vibration API. Haptics stay on under Reduce Motion.
  const vibratePfScrollWarranty = (isPaid) => {
    if (typeof navigator.vibrate !== "function") return;
    try {
      if (isPaid && !pfScrollWarrantyPaidHapticDone) {
        pfScrollWarrantyPaidHapticDone = true;
        navigator.vibrate([12, 40, 18]);
        return;
      }
      navigator.vibrate(8);
    } catch {
      // Haptics are decoration; never let them break the selection.
    }
  };

  const finishPfScrollWarrantyDateRoll = (mirror) => {
    const roll = pfScrollWarrantyDateRolls.get(mirror);
    if (!roll) return;
    pfScrollWarrantyDateRolls.delete(mirror);
    cancelAnimationFrame(roll.frame);
    roll.el.textContent = roll.finalText;
    roll.el.classList.remove("is-rolling");
  };

  /** Counts the coverage date up month by month from today, then lands on the exact end date. */
  const rollPfScrollWarrantyDate = (mirror, el) => {
    finishPfScrollWarrantyDateRoll(mirror);
    const months = Number(el.dataset.pfScrollWarrantyDateMonths);
    if (!months || prefersPfScrollReducedMotion()) return;
    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || "en", {
      month: "short",
      year: "numeric",
    });
    const duration = 520 + (160 * months) / 12;
    const start = performance.now();
    const roll = { el, finalText: el.textContent, frame: 0 };
    const step = (now) => {
      if (pfScrollWarrantyDateRolls.get(mirror) !== roll) return;
      const progress = Math.min(1, (now - start) / duration);
      if (progress >= 1 || !el.isConnected) {
        finishPfScrollWarrantyDateRoll(mirror);
        return;
      }
      const date = new Date();
      date.setMonth(date.getMonth() + Math.round((1 - (1 - progress) ** 3) * months));
      el.textContent = formatter.format(date);
      roll.frame = requestAnimationFrame(step);
    };
    el.classList.add("is-rolling");
    pfScrollWarrantyDateRolls.set(mirror, roll);
    roll.frame = requestAnimationFrame(step);
  };

  /** A gradient wash from the finger, above the card's white face and below its text. */
  const startPfScrollWarrantyRipple = (item, event) => {
    if (prefersPfScrollReducedMotion()) return;
    let layer = item.querySelector(":scope > .pf-scroll-warranty-selector__ripple-layer");
    if (!layer) {
      layer = document.createElement("span");
      layer.className = "pf-scroll-warranty-selector__ripple-layer";
      layer.setAttribute("aria-hidden", "true");
      item.prepend(layer);
    }
    const rect = item.getBoundingClientRect();
    const size = rect.width * 2.2;
    const ripple = document.createElement("span");
    ripple.className = "pf-scroll-warranty-selector__ripple";
    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    ripple.style.left = `${event.clientX - rect.left - item.clientLeft - size / 2}px`;
    ripple.style.top = `${event.clientY - rect.top - item.clientTop - size / 2}px`;
    const remove = () => ripple.remove();
    ripple.addEventListener("animationend", remove, { once: true });
    window.setTimeout(remove, 1000);
    layer.appendChild(ripple);
  };

  /** "Protected through {date}" under the cards while a paid plan is selected. */
  const syncPfScrollWarrantyProtectedRow = (mirror, quote, choice, animate) => {
    const row = mirror.querySelector("[data-pf-scroll-warranty-protected]");
    if (!row) return;
    const plan = choice?.isNoCoverage
      ? null
      : quote.plans.find((candidate) => candidate.quoteItemId === choice?.quoteItemId);
    const coverageDate = plan ? getPfScrollWarrantyCoverageDate(plan.years) : "";
    const isOpen = Boolean(coverageDate);
    const text = row.querySelector("[data-pf-scroll-warranty-protected-text]");
    if (isOpen && text) {
      const label = (mirror.dataset.pfScrollWarrantyProtectedThrough || "__DATE__").replace(
        "__DATE__",
        coverageDate
      );
      if (text.textContent !== label) text.textContent = label;
    }
    if (row.classList.contains("is-open") === isOpen) return;
    row.classList.toggle("is-instant", !animate || prefersPfScrollReducedMotion());
    row.classList.toggle("is-open", isOpen);
  };

  /**
   * Plays the tap effects on the newly selected card. Every card loses its tapped marker first, so
   * the newest tap always wins and no card is left half drawn: at rest each card shows its final
   * state, and the CSS animations only run while the marker is on.
   */
  const runPfScrollWarrantySelectionMotion = (mirror, list, selectedIndex) => {
    const items = Array.from(list.children);
    const selected = items[selectedIndex];
    const isPaid = Boolean(
      selected && !selected.classList.contains("pf-scroll-warranty-selector__item--decline")
    );
    vibratePfScrollWarranty(isPaid);
    items.forEach((item) => item.removeAttribute("data-pf-scroll-warranty-tapped"));
    finishPfScrollWarrantyDateRoll(mirror);
    if (!isPaid || prefersPfScrollReducedMotion()) return;
    selected.setAttribute("data-pf-scroll-warranty-tapped", "");
    const date = selected.querySelector("[data-pf-scroll-warranty-date-months]");
    if (date) rollPfScrollWarrantyDate(mirror, date);
  };

  const renderPfScrollWarrantyCurrentList = (mirror, list, quote, choice) => {
    const entries = [...quote.plans, null];
    const selectedIndex = entries.findIndex((plan) =>
      plan ? plan.quoteItemId === choice.quoteItemId : choice.isNoCoverage
    );
    const renderKey = [
      mirror.dataset.pfScrollWarrantyDecline || "",
      mirror.dataset.pfScrollWarrantyMostPopular || "",
      mirror.dataset.pfScrollWarrantyPerMonth || "",
      mirror.dataset.pfScrollWarrantyPlanTitle || "",
      mirror.dataset.pfScrollWarrantyPlanDescription || "",
      ...quote.plans.map(
        (plan) => `${plan.quoteItemId}:${plan.years}:${plan.priceCents}:${plan.ribbon}`
      ),
    ].join("|");
    syncPfScrollWarrantyExperimentInfo(mirror, quote, true);
    const previousIndex = Array.from(list.children).findIndex((item) =>
      item.classList.contains("is-selected")
    );
    if (
      mirror.dataset.pfScrollWarrantyRenderKey !== renderKey ||
      list.children.length !== entries.length
    ) {
      mirror.dataset.pfScrollWarrantyRenderKey = renderKey;
      list.innerHTML = "";
      entries.forEach((plan, index) => {
        list.appendChild(buildPfScrollWarrantyCurrentCard(mirror, plan, index));
      });
    }
    Array.from(list.children).forEach((item) => {
      syncPfScrollWarrantyItemState(item, selectedIndex, choice);
    });
    if (isPfScrollWarrantyMotion(mirror)) {
      const tapped = pfScrollWarrantyMotionTap === mirror && previousIndex !== selectedIndex;
      syncPfScrollWarrantyProtectedRow(mirror, quote, choice, tapped);
      if (tapped) runPfScrollWarrantySelectionMotion(mirror, list, selectedIndex);
    }
    mirror.hidden = false;
  };

  const renderPfScrollWarrantySelector = (section, quote) => {
    const suppressWarranty = shouldSuppressPfScrollWarranty(section);
    const canRevealWarranty = quote?.status === "ok" && quote.plans.length > 0 && !suppressWarranty;
    const isPending = section.dataset.pfScrollWarrantyEligible === "pending";
    const choice = canRevealWarranty ? resolvePfScrollWarrantyChoice(section, quote) : null;
    // The quick style's skeleton keeps the row in place while the quote resolves; the current style
    // keeps production's behavior and reveals the row only once plans exist.
    const showSkeleton =
      isPending && !suppressWarranty && isPfScrollWarrantyQuick(getPfScrollWarrantyMirror(section));
    syncPfScrollWarrantyRowVisibility(section, canRevealWarranty || showSkeleton, suppressWarranty);
    document.body.classList.toggle(
      "pf-scroll-warranty-mirror-active",
      getLivePfScrollSections().some(
        (candidate) => candidate.dataset.pfScrollWarrantyEligible === "true"
      )
    );

    section.querySelectorAll(selectors.warrantySelector).forEach((mirror) => {
      const list = mirror.querySelector("[data-pf-scroll-warranty-list]");
      if (!list) return;
      if (!canRevealWarranty) {
        delete mirror.dataset.pfScrollWarrantyRenderKey;
        if (showSkeleton && isPfScrollWarrantyQuick(mirror)) {
          renderPfScrollWarrantySkeleton(mirror, list);
          syncPfScrollWarrantyExperimentInfo(mirror, null, false);
          syncPfScrollWarrantyPendingCopy(mirror, true);
          mirror.hidden = false;
          return;
        }
        mirror.hidden = true;
        list.innerHTML = "";
        clearPfScrollWarrantySkeleton(list);
        return;
      }

      if (!isPfScrollWarrantyQuick(mirror)) {
        renderPfScrollWarrantyCurrentList(mirror, list, quote, choice);
        return;
      }

      const framing = getPfScrollWarrantyPriceFraming(mirror);
      const breakdownOnly = isPfScrollWarrantyBreakdownOnly(quote);
      const activePlan =
        (!choice.isNoCoverage &&
          quote.plans.find((plan) => plan.quoteItemId === choice.quoteItemId)) ||
        getPfScrollWarrantyPreferredPlan(quote);
      const entries = [activePlan, null];
      const selectedIndex = choice.isNoCoverage ? 1 : 0;
      const renderKey = [
        mirror.dataset.pfScrollWarrantyDecline || "",
        mirror.dataset.pfScrollWarrantyMostPopular || "",
        mirror.dataset.pfScrollWarrantyPerMonth || "",
        mirror.dataset.pfScrollWarrantyPlanCardTitle || "",
        mirror.dataset.pfScrollWarrantyPlanSubtitle || "",
        framing,
        breakdownOnly ? "B" : "AD",
        activePlan?.quoteItemId || "",
        ...quote.plans.map(
          (plan) => `${plan.quoteItemId}:${plan.years}:${plan.priceCents}:${plan.ribbon}`
        ),
      ].join("|");
      syncPfScrollWarrantyExperimentInfo(mirror, quote, true);
      syncPfScrollWarrantyPendingCopy(mirror, false);
      clearPfScrollWarrantySkeleton(list);
      if (
        mirror.dataset.pfScrollWarrantyRenderKey !== renderKey ||
        list.children.length !== entries.length
      ) {
        mirror.dataset.pfScrollWarrantyRenderKey = renderKey;
        list.innerHTML = "";
        entries.forEach((plan, index) => {
          list.appendChild(
            buildPfScrollWarrantyExperimentCard(mirror, plan, index, {
              breakdownOnly,
              framing,
              quote,
            })
          );
        });
      }
      Array.from(list.children).forEach((item) => {
        syncPfScrollWarrantyItemState(item, selectedIndex, choice);
      });
      syncPfScrollWarrantyPanelCopy(section, mirror, quote, activePlan, choice);
      mirror.hidden = false;
    });
  };

  const publishPfScrollWarrantyQuote = (section, key, quote, eligibility) => {
    section.__pfWarrantyQuote = quote ? { key, quote } : null;
    section.dataset.pfScrollWarrantyEligible = eligibility;
  };

  /**
   * Fetches the SureBright quote for the variant on screen and renders it. Repeat calls for the
   * same product and variant only re-apply state, so the variant refresh timers can call freely.
   */
  const syncPfScrollWarrantySelector = (section) => {
    if (!section.querySelector(selectors.warrantySelector)) return;
    const quotes = window.PfSureBrightQuotes;
    const context = getPfScrollWarrantyContext(section);
    const suppressWarranty = shouldSuppressPfScrollWarranty(section);
    const warrantyMirror = getPfScrollWarrantyMirror(section);
    if (warrantyMirror && isPfScrollWarrantyAppHost()) warrantyMirror.dataset.pfSbEnabled = "false";
    const selectorEnabled = warrantyMirror?.dataset?.pfSbEnabled !== "false";
    const canQuote =
      quotes &&
      selectorEnabled &&
      !suppressWarranty &&
      context.shop &&
      context.productId &&
      context.variantId;
    const key = canQuote ? quotes.cacheKey(context) : "";
    const state = pfScrollWarrantyQuoteStates.get(section);

    // A quote-less state whose request was aborted can never resolve, so it must not hold the row
    // in its loading state; fall through and ask again.
    const stateIsDead = Boolean(state && !state.quote && state.controller?.signal?.aborted);
    if (state?.key === key && !stateIsDead) {
      if (state.quote || !key) renderPfScrollWarrantySelector(section, state.quote);
      return;
    }

    state?.controller?.abort();
    const controller = key ? new AbortController() : null;
    const nextState = { controller, key, quote: null };
    pfScrollWarrantyQuoteStates.set(section, nextState);
    publishPfScrollWarrantyQuote(section, key, null, key ? "pending" : "false");
    renderPfScrollWarrantySelector(section, null);
    if (!key) return;

    quotes
      .fetchPlans({ ...context, signal: controller.signal })
      .then((quote) => {
        if (pfScrollWarrantyQuoteStates.get(section) !== nextState) return;
        nextState.quote = quote;
        const eligible = quote.status === "ok" && quote.plans.length > 0;
        publishPfScrollWarrantyQuote(section, key, quote, eligible ? "true" : "false");
        renderPfScrollWarrantySelector(section, quote);
        const ids = { product_id: context.productId, variant_id: context.variantId };
        if (eligible) {
          quotes.track("plug_warranty_plans_shown", {
            ...ids,
            cache_hit: Boolean(quote.cacheHit),
            fetch_ms: quote.fetchMs ?? 0,
            plan_count: quote.plans.length,
            prices_cents: quote.plans.map((plan) => plan.priceCents),
            years: quote.plans.map((plan) => Number(plan.years)),
          });
        } else if (quote.status === "error") {
          quotes.track("plug_warranty_quote_fetch_failed", {
            ...ids,
            fetch_ms: quote.fetchMs ?? 0,
            reason: quote.failureReason || "unknown",
          });
        }
      })
      .catch(() => {
        // Aborted because the product or variant changed; the newer request owns the row.
        if (pfScrollWarrantyQuoteStates.get(section) !== nextState || controller.signal.aborted)
          return;
        // Someone else's abort rejected this request while the row still waits on it. Ask once more
        // so the shopper sees a price instead of a loading row.
        if (nextState.isRetry) return;
        pfScrollWarrantyQuoteStates.delete(section);
        syncPfScrollWarrantySelector(section);
        const retryState = pfScrollWarrantyQuoteStates.get(section);
        if (retryState) retryState.isRetry = true;
      });
  };

  let pfScrollWarrantyDrawerReturnFocus = null;

  const closePfScrollWarrantyDrawer = (drawer) => {
    if (!drawer || !drawer.classList.contains("active")) return;
    drawer.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
    window.setTimeout(() => {
      if (!drawer.classList.contains("active")) drawer.setAttribute("hidden", "");
    }, 280);
    const returnTo = pfScrollWarrantyDrawerReturnFocus;
    pfScrollWarrantyDrawerReturnFocus = null;
    returnTo?.focus?.();
  };

  const openPfScrollWarrantyDrawer = (drawer, trigger) => {
    if (!drawer) return;
    pfScrollWarrantyDrawerReturnFocus = trigger || null;
    drawer.removeAttribute("hidden");
    window.requestAnimationFrame(() => {
      drawer.classList.add("active");
      document.body.classList.add("overflow-hidden");
      drawer.querySelector("[data-pf-scroll-warranty-drawer-close]")?.focus?.();
    });
  };

  const getPfScrollWarrantyFocusable = (drawer) =>
    Array.from(drawer.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])')).filter(
      (node) => !node.hidden && node.offsetParent !== null
    );

  const trapPfScrollWarrantyDrawerFocus = (drawer, event) => {
    if (event.key !== "Tab") return;
    const focusable = getPfScrollWarrantyFocusable(drawer);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** Applies the choice a card or a term pill carries. Shared by the click and keyboard paths. */
  const applyPfScrollWarrantyChoice = (section, option) => {
    const quote = pfScrollWarrantyQuoteStates.get(section)?.quote;
    if (quote?.status !== "ok") return;
    const value = option.dataset.pfScrollWarrantyChoiceValue;
    const plan = quote.plans.find((candidate) => candidate.quoteItemId === value);
    if (!plan && value !== PF_SCROLL_NO_COVERAGE) return;
    const variantId = String(quote.variantId);
    setPfScrollExplicitWarrantyChoice(
      section,
      plan ? { quoteItemId: plan.quoteItemId, variantId, years: plan.years } : { variantId }
    );
    renderPfScrollWarrantySelector(section, quote);
    window.PfSureBrightQuotes?.track("plug_warranty_plan_selected", {
      no_coverage: !plan,
      price_cents: plan?.priceCents ?? 0,
      product_id: String(quote.productId),
      quote_item_id: plan?.quoteItemId || "",
      term_picker: option.hasAttribute("data-pf-scroll-warranty-term"),
      ui_version: getPfScrollWarrantyUiVersion(getPfScrollWarrantyMirror(section)),
      variant_id: variantId,
      years: plan ? Number(plan.years) : 0,
    });
  };

  const observePfScrollWarrantyRow = (section, lifecycleSignal) => {
    const mirror = getPfScrollWarrantyMirror(section);
    if (!mirror || typeof IntersectionObserver !== "function") return;
    let reported = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (reported || !entries.some((entry) => entry.isIntersecting)) return;
        if (mirror.hidden) return;
        reported = true;
        observer.disconnect();
        const quote = pfScrollWarrantyQuoteStates.get(section)?.quote;
        window.PfSureBrightQuotes?.track("plug_warranty_row_viewed", {
          plan_count: quote?.plans?.length || 0,
          product_id: String(quote?.productId || ""),
          ui_version: getPfScrollWarrantyUiVersion(mirror),
          variant_id: String(quote?.variantId || ""),
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(mirror);
    lifecycleSignal?.addEventListener("abort", () => observer.disconnect(), { once: true });
  };

  const initPfScrollWarrantySelectors = (section, lifecycleSignal) => {
    syncPfScrollWarrantySelector(section);
    observePfScrollWarrantyRow(section, lifecycleSignal);

    section.addEventListener(
      "click",
      (event) => {
        const drawer = section.querySelector("[data-pf-scroll-warranty-drawer]");

        const drawerClose = event.target.closest("[data-pf-scroll-warranty-drawer-close]");
        if (drawerClose && section.contains(drawerClose)) {
          event.preventDefault();
          closePfScrollWarrantyDrawer(drawer);
          return;
        }

        const compare = event.target.closest("[data-pf-scroll-warranty-compare]");
        if (compare && section.contains(compare)) {
          event.preventDefault();
          openPfScrollWarrantyDrawer(drawer, compare);
          const quote = pfScrollWarrantyQuoteStates.get(section)?.quote;
          window.PfSureBrightQuotes?.track("plug_warranty_compare_opened", {
            product_id: String(quote?.productId || ""),
            ui_version: getPfScrollWarrantyUiVersion(getPfScrollWarrantyMirror(section)),
            variant_id: String(quote?.variantId || ""),
          });
          return;
        }

        const warrantyOption = event.target.closest("[data-pf-scroll-warranty-choice-value]");
        if (warrantyOption && section.contains(warrantyOption)) {
          event.preventDefault();
          if (!event.isTrusted) return;
          pfScrollWarrantyMotionTap = warrantyOption.closest(selectors.warrantySelector);
          try {
            applyPfScrollWarrantyChoice(section, warrantyOption);
          } finally {
            pfScrollWarrantyMotionTap = null;
          }
          return;
        }

        const learnMore = event.target.closest("[data-pf-scroll-warranty-learn-more]");
        if (learnMore && section.contains(learnMore)) {
          event.preventDefault();
          const learnMoreUrl = pfScrollWarrantyQuoteStates.get(section)?.quote?.learnMoreUrl;
          if (learnMoreUrl) window.open(learnMoreUrl, "_blank", "noopener");
        }
      },
      { signal: lifecycleSignal }
    );

    section.addEventListener(
      "pointerdown",
      (event) => {
        if (!event.isTrusted || event.button > 0) return;
        const item = event.target.closest?.(
          "[data-pf-scroll-warranty-list] [data-pf-scroll-warranty-choice-value]"
        );
        if (!item || !section.contains(item)) return;
        if (!isPfScrollWarrantyMotion(item.closest(selectors.warrantySelector))) return;
        startPfScrollWarrantyRipple(item, event);
      },
      { passive: true, signal: lifecycleSignal }
    );

    section.addEventListener(
      "keydown",
      (event) => {
        const drawer = section.querySelector("[data-pf-scroll-warranty-drawer].active");
        if (drawer) {
          if (event.key === "Escape") {
            event.preventDefault();
            closePfScrollWarrantyDrawer(drawer);
            return;
          }
          trapPfScrollWarrantyDrawerFocus(drawer, event);
          return;
        }

        const radio = event.target.closest?.('[data-pf-scroll-warranty-list] [role="radio"]');
        if (!radio || !section.contains(radio)) return;
        const delta =
          event.key === "ArrowDown" || event.key === "ArrowRight"
            ? 1
            : event.key === "ArrowUp" || event.key === "ArrowLeft"
            ? -1
            : 0;
        if (!delta) return;
        event.preventDefault();
        const radios = Array.from(
          section.querySelectorAll('[data-pf-scroll-warranty-list] [role="radio"]')
        );
        const index = radios.indexOf(radio);
        if (index === -1 || radios.length < 2) return;
        const next = radios[(index + delta + radios.length) % radios.length];
        const option = next.closest("[data-pf-scroll-warranty-choice-value]");
        if (!option) return;
        applyPfScrollWarrantyChoice(section, option);
        section
          .querySelector('[data-pf-scroll-warranty-list] [role="radio"][aria-checked="true"]')
          ?.focus?.();
      },
      { signal: lifecycleSignal }
    );

    lifecycleSignal?.addEventListener(
      "abort",
      () => {
        const state = pfScrollWarrantyQuoteStates.get(section);
        state?.controller?.abort();
        if (!state?.quote) pfScrollWarrantyQuoteStates.delete(section);
      },
      { once: true }
    );
  };

  const refreshAllPfScrollVariantBlocks = () => {
    getLivePfScrollSections().forEach((scrollSection) => {
      syncPfScrollProductInfoState(scrollSection);
      syncPfScrollComplementarySelectorState(scrollSection);
      syncPfScrollWarrantySelector(scrollSection);
      const refresh = variantSectionRefreshers.get(scrollSection);
      if (refresh) refresh();
    });
  };

  /** variant-selects may morph async; product-info swap removes old node after ~500ms. */
  const schedulePfScrollVariantBlockRefresh = () => {
    refreshAllPfScrollVariantBlocks();
    window.requestAnimationFrame(() => {
      refreshAllPfScrollVariantBlocks();
    });
    [50, 150, 400, 600].forEach((delayMs) => {
      window.setTimeout(refreshAllPfScrollVariantBlocks, delayMs);
    });
  };

  const findPfScrollRadioForVariantOption = (fieldset, optionIndex) => {
    if (!fieldset || !optionIndex) return null;
    const variant = getPfScrollSelectedVariant(
      fieldset.closest("pf-pilot-product-info") || fieldset.closest("product-info")
    );
    const optionValue = variant?.[`option${optionIndex}`];
    if (!optionValue) return null;
    return (
      Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === optionValue
      ) || null
    );
  };

  const getLiveCheckedRadioInFieldset = (fieldset, optionIndex = 0) => {
    if (!fieldset) return null;

    const resolvedOptionIndex = optionIndex || Number(fieldset.dataset.optionIndex) || 0;

    const byPseudo = fieldset.querySelector('input[type="radio"]:checked');
    if (byPseudo) return byPseudo;

    const byAttr = fieldset.querySelector('input[type="radio"][checked]');
    if (byAttr) return byAttr;

    const variantSelects = fieldset.closest("variant-selects");
    const selectedIds = variantSelects?.selectedOptionValues;
    if (selectedIds?.length) {
      const matched = Array.from(
        fieldset.querySelectorAll('input[type="radio"][data-option-value-id]')
      ).find((input) => selectedIds.includes(input.dataset.optionValueId));
      if (matched) return matched;
    }

    return findPfScrollRadioForVariantOption(fieldset, resolvedOptionIndex);
  };

  const applyPfScrollRadioSelection = (root, selectedInput) => {
    if (!root) return;
    const selectedValue = selectedInput?.value ?? "";
    const selectedOptionValueId = selectedInput?.dataset?.optionValueId ?? "";

    root.querySelectorAll('input[type="radio"]').forEach((input) => {
      const isSelected =
        Boolean(selectedInput) &&
        ((selectedOptionValueId && input.dataset.optionValueId === selectedOptionValueId) ||
          (selectedValue && input.value === selectedValue));

      input.checked = isSelected;
      if (isSelected) {
        input.setAttribute("checked", "checked");
      } else {
        input.removeAttribute("checked");
      }

      const label = input.nextElementSibling?.classList?.contains("swatch-input__label")
        ? input.nextElementSibling
        : root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      label?.classList.toggle("is-selected", isSelected);
    });
  };

  const syncPfScrollVariantSourcesToResolvedVariant = (variant) => {
    if (!variant?.id) return;
    const optionValues = Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter(
          (optionValue) => optionValue != null
        );
    if (!optionValues.length) return;

    getLivePfScrollSections().forEach((section) => {
      const resolvedProductId = String(variant.product_id || variant.productId || "");
      const sectionProductId = String(getPfScrollProductId(section) || "");
      if (resolvedProductId && sectionProductId && resolvedProductId !== sectionProductId) return;

      getProductInfoVariantSelectSources(section).forEach((source) => {
        optionValues.forEach((optionValue, optionIndex) => {
          const fieldset = findFieldsetByOptionIndex(source, optionIndex + 1);
          if (!fieldset) return;
          const matchingInput = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === String(optionValue)
          );
          if (!matchingInput) return;

          applyPfScrollRadioSelection(fieldset, matchingInput);
          const selectedValueLabel = fieldset.querySelector(".selected-swatch-value");
          if (selectedValueLabel) selectedValueLabel.textContent = matchingInput.value;
        });
      });
    });
  };

  const syncPfScrollRadioCloneCheckedFromLive = (clone, liveFieldset, optionIndex = 0) => {
    const resolvedOptionIndex = optionIndex || Number(liveFieldset?.dataset?.optionIndex) || 0;
    let liveChecked = getLiveCheckedRadioInFieldset(liveFieldset, resolvedOptionIndex);
    if (!liveChecked) {
      liveChecked = findPfScrollRadioForVariantOption(liveFieldset, resolvedOptionIndex);
    }
    if (liveChecked && !liveFieldset.querySelector('input[type="radio"]:checked')) {
      applyPfScrollRadioSelection(liveFieldset, liveChecked);
    }
    applyPfScrollRadioSelection(clone, liveChecked);

    const liveSwatchValue = liveFieldset.querySelector(".selected-swatch-value");
    const cloneSwatchValue = clone.querySelector(".selected-swatch-value");
    if (liveChecked && liveSwatchValue) {
      liveSwatchValue.textContent = liveChecked.value;
    }
    if (liveChecked && cloneSwatchValue) {
      cloneSwatchValue.textContent = liveChecked.value;
    } else if (liveSwatchValue && cloneSwatchValue) {
      cloneSwatchValue.textContent = liveSwatchValue.textContent;
    }
  };

  const stashPostAjaxVariantScrollTarget = (
    section,
    variantOptionBlock,
    willRedirect,
    guidedStep = null
  ) => {
    if (willRedirect) return;
    const shopifySectionEl = section.closest('[id^="shopify-section-"]');
    try {
      sessionStorage.setItem(
        POST_AJAX_VARIANT_SCROLL_STASH_KEY,
        JSON.stringify({
          sectionId: shopifySectionEl?.id || "",
          variantOptionBlock: variantOptionBlock || "",
          ...(guidedStep ? { guidedStep } : {}),
        })
      );
    } catch {
      // ignore
    }
  };

  const consumePostAjaxVariantScrollStash = () => {
    let raw;
    try {
      raw = sessionStorage.getItem(POST_AJAX_VARIANT_SCROLL_STASH_KEY);
      if (!raw) return;
      sessionStorage.removeItem(POST_AJAX_VARIANT_SCROLL_STASH_KEY);
    } catch {
      return;
    }

    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }

    const { sectionId, variantOptionBlock, guidedStep } = payload;
    // Guided option steps (templates/product.guided-options.json) move on to the next option
    // instead of re-centering the one just picked.
    if (guidedStep && window.PfScrollGuidedSteps?.consume(guidedStep)) return;
    scrollPfVariantOptionIntoView(sectionId, variantOptionBlock);
  };

  let productInfoChangeAbort = new AbortController();
  let scrollSectionLifecycleAbort = new AbortController();
  let variantChangeUnsubscribe = null;

  const bindProductInfoChangeToLiveElement = () => {
    productInfoChangeAbort.abort();
    productInfoChangeAbort = new AbortController();
    const { signal } = productInfoChangeAbort;

    const productInfos = [
      ...document.querySelectorAll("product-info"),
      ...document.querySelectorAll("pf-pilot-product-info"),
    ].filter((productInfo) => {
      if (productInfo.matches("[data-pf-scroll-product-info]")) return true;
      return Boolean(productInfo.querySelector(".product__info-container"));
    });

    productInfos.forEach((productInfo) => {
      productInfo.addEventListener(
        "change",
        (event) => {
          const vs = event.target?.closest?.('variant-selects:not([data-upsell-isolated="true"])');
          if (!vs || !productInfo.contains(vs)) return;
          const infoCol = productInfo.querySelector(".product__info-container");
          if (infoCol && !infoCol.contains(vs)) return;
          schedulePfScrollVariantBlockRefresh();
        },
        { signal }
      );
    });
  };

  const updatePfScrollDetachedPrices = (variant) => {
    if (!variant) return;
    getLivePfScrollSections().forEach((section) => {
      updatePfScrollVisiblePrices(section, variant);
      updatePfScrollProductBadges(section, variant);
    });
  };

  const refreshPfScrollWishlistButtons = (
    root = document,
    variant = pfScrollLastPublishedVariant
  ) => {
    getLivePfScrollSections(root).forEach((section) => {
      const { productId, variantId, productUrl } = getPfScrollProductContext(section, variant);
      const productPath = getPfScrollComparableProductUrl(productUrl);
      // Rivo hearts (snippets/pf-wish-button.liquid) and any leftover Swym markup.
      section.querySelectorAll("[data-pf-wish], .swym-add-to-wishlist").forEach((button) => {
        Array.from(button.classList)
          .filter((className) => /^product_\d+$/.test(className))
          .forEach((className) => button.classList.remove(className));
        if (productId) {
          button.dataset.productId = productId;
          button.classList.add(`product_${productId}`);
        }
        if (variantId) button.dataset.variantId = variantId;
        if (productPath) {
          const wishlistUrl = new URL(
            button.dataset.productUrl || window.location.origin,
            window.location.origin
          );
          wishlistUrl.pathname = productPath;
          wishlistUrl.search = "";
          wishlistUrl.hash = "";
          button.dataset.productUrl = wishlistUrl.toString();
        }
      });
    });
    // Swym's script can throw here (its 10/2026 build reads an undefined loadSecondaryStyles after a
    // product swap). This runs inside the variantChange handler, so a throw used to skip the option
    // row rebuild and leave Color, Storage and Condition empty. The wishlist must never block that.
    try {
      // eslint-disable-next-line no-underscore-dangle -- Swym exposes this vendor global
      window._swat?.initializeActionButtons?.(`${selectors.section} .swym-add-to-wishlist`);
    } catch (error) {
      debugPfScrollVariantSelectors("swym initializeActionButtons failed", {
        message: error?.message || String(error),
      });
    }
  };

  const onGlobalVariantChangeForProductScroll = () => {
    window.requestAnimationFrame(() => {
      applyMainVariantVisibilityFromDom();
      preservePfScrollComplementaryStateForLiveSections();

      scrollSectionLifecycleAbort.abort();
      scrollSectionLifecycleAbort = new AbortController();
      const lifecycleSignal = scrollSectionLifecycleAbort.signal;

      getLivePfScrollSections().forEach((section) => {
        // eslint-disable-next-line no-use-before-define -- initProductScroll is a hoisted function declaration
        initProductScroll(section, lifecycleSignal);
      });

      syncPfScrollGalleryMainModals();
      // eslint-disable-next-line no-use-before-define -- clamp helper is initialized before variantChange fires
      clampPfScrollDescriptions(document, { resetExpanded: true });
      refreshPfScrollWishlistButtons(document);

      schedulePfScrollVariantBlockRefresh();

      window.setTimeout(() => {
        consumePostAjaxVariantScrollStash();
      }, 0);

      bindProductInfoChangeToLiveElement();
    });
  };

  let optionValueSelectionUnsubscribe = null;

  const bindProductInfoVariantIntegrationOnce = () => {
    bindProductInfoChangeToLiveElement();

    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      !variantChangeUnsubscribe
    ) {
      variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        restorePfScrollPilotViewQuery();
        pfScrollLastPublishedVariant = event?.data?.variant ?? null;
        syncPfScrollVariantSourcesToResolvedVariant(pfScrollLastPublishedVariant);
        updatePfScrollProductFormsVariantId(pfScrollLastPublishedVariant?.id);
        updatePfScrollDetachedPrices(pfScrollLastPublishedVariant);
        updatePfScrollBuyButtons(
          event?.data?.html,
          event?.data?.sectionId,
          pfScrollLastPublishedVariant
        );
        refreshPfScrollWishlistButtons(document, pfScrollLastPublishedVariant);
        emitResolvedPfScrollOptionAnalytics(pfScrollLastPublishedVariant);
        onGlobalVariantChangeForProductScroll();
      });
    }

    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      !optionValueSelectionUnsubscribe
    ) {
      optionValueSelectionUnsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        window.requestAnimationFrame(() => {
          schedulePfScrollVariantBlockRefresh();
        });
      });
    }
  };

  const desktopMediaQuery = window.matchMedia("(min-width: 960px)");
  const swapTriggerRatio = 0.4;

  /**
   * Sticky panel top/height use this offset. Prefer live header geometry — theme header.js
   * toggles --header-element-height on scroll (full stack vs sticky-only), which makes the
   * panel height jump when pf-scroll re-reads that variable on every scroll.
   */
  const getHeaderOffset = () => 60;

  const setSectionHeaderOffset = (section) => {
    section.style.setProperty("--pf-scroll-header-offset", `${getHeaderOffset()}px`);
  };

  const getHeaderWatchTargets = () => {
    const targets = [];
    const stickyHeader = document.querySelector("sticky-header");
    const headerWrapper = document.querySelector(".header-wrapper");
    const announcementBar = document.querySelector("section-announcement-bar");
    const fuegoBanner = document.querySelector(".fuego-chat-banner");

    [stickyHeader, headerWrapper, announcementBar, fuegoBanner].forEach((node) => {
      if (node) targets.push(node);
    });

    return targets;
  };

  const isPfScrollLeftPanelMediaMeaningful = (itemMedia) => {
    if (!itemMedia) return false;
    if (
      itemMedia.querySelector(
        '.pf-scroll-review-card__inner--image-only[style*="--pf-review-bg-image"]'
      )
    ) {
      return true;
    }
    if (
      itemMedia.querySelector(
        'img[src]:not([src=""]), picture, video, svg, iframe, canvas, source[srcset]'
      )
    ) {
      return true;
    }
    const kids = Array.from(itemMedia.children).filter(
      (c) => c.tagName !== "SCRIPT" && c.tagName !== "STYLE"
    );
    if (kids.length === 0) {
      return Boolean(itemMedia.textContent && itemMedia.textContent.trim());
    }
    return kids.some((c) => {
      if (["IMG", "PICTURE", "VIDEO", "SVG", "IFRAME", "CANVAS"].includes(c.tagName)) return true;
      if (c.querySelector("img, picture, video, svg")) return true;
      return Boolean(c.textContent && c.textContent.trim());
    });
  };

  const itemMediaIsPfScrollReviewSlot = (itemMedia) =>
    Boolean(itemMedia?.querySelector("[data-pf-scroll-review-slot]"));

  const itemMediaHasPopulatedPfScrollReviewSlot = (itemMedia) =>
    Boolean(
      itemMedia?.querySelector(
        "[data-pf-scroll-review-slot]:not(.pf-product-scroll__review-slot--empty)"
      )
    );

  const syncPfScrollItemMediaLayoutClasses = (root = document) => {
    root.querySelectorAll(".pf-product-scroll__item-media").forEach((itemMedia) => {
      const hasMeaningfulMedia = isPfScrollLeftPanelMediaMeaningful(itemMedia);
      const hasReview = itemMediaHasPopulatedPfScrollReviewSlot(itemMedia);
      const hasImageBlock = Boolean(itemMedia.querySelector(".pf-product-scroll__image-block"));
      const hasSlider = Boolean(
        itemMedia.querySelector(
          ".pf-product-gallery--theme, .pf-product-gallery--simple, slider-component"
        )
      );
      itemMedia.classList.toggle("pf-product-scroll__item-media--review", hasReview);
      itemMedia.classList.toggle("pf-product-scroll__item-media--standard", !hasReview);
      itemMedia.classList.toggle("pf-product-scroll__item-media--image-block", hasImageBlock);
      itemMedia.classList.toggle("pf-product-scroll__item-media--slider", hasSlider);
      itemMedia.classList.toggle("pf-product-scroll__item-media--empty", !hasMeaningfulMedia);
    });
  };

  const PF_SCROLL_REVIEW_STASH_PREFIX = "pf-pilot-product-scroll-review-slot:v3:";

  const getPfScrollShopifySectionRootId = (section) =>
    section.closest('[id^="shopify-section-"]')?.id || "";

  const getPfScrollReviewStashKey = (section) => {
    const shopifySectionId = getPfScrollShopifySectionRootId(section);
    const productId = getPfScrollProductId(section);
    return shopifySectionId && productId ? `${shopifySectionId}:${productId}` : "";
  };

  const loadPfScrollReviewStash = (stashKey) => {
    if (!stashKey) return {};
    try {
      const raw = sessionStorage.getItem(`${PF_SCROLL_REVIEW_STASH_PREFIX}${stashKey}`);
      if (!raw) return {};
      const data = JSON.parse(raw);
      return data && typeof data === "object" && !Array.isArray(data) ? data : {};
    } catch {
      return {};
    }
  };

  const savePfScrollReviewStash = (stashKey, stash) => {
    if (!stashKey) return;
    try {
      sessionStorage.setItem(`${PF_SCROLL_REVIEW_STASH_PREFIX}${stashKey}`, JSON.stringify(stash));
    } catch {
      // ignore quota / private mode
    }
  };

  const clonePfScrollItemMediaFromStashedOuterHtml = (outerHtml) => {
    if (!outerHtml || typeof outerHtml !== "string") return null;
    const wrap = document.createElement("div");
    wrap.innerHTML = outerHtml.trim();
    const first = wrap.firstElementChild;
    return first ? first.cloneNode(true) : null;
  };

  const collectPfScrollCloneElements = (root) => {
    const elements = [];
    const visit = (scope) => {
      if (!scope?.querySelectorAll) return;
      if (scope.nodeType === 1) elements.push(scope);
      scope.querySelectorAll("*").forEach((element) => {
        elements.push(element);
        if (element.tagName === "TEMPLATE") visit(element.content);
      });
    };
    visit(root);
    return elements;
  };

  const normalizePfScrollCloneNamespace = (value) =>
    String(value || "pf-clone")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "pf-clone";

  const remapPfScrollCloneLocalReferences = (root, section, rowIndex) => {
    if (!root) return;
    const cloneNamespace = normalizePfScrollCloneNamespace(
      `pf-clone-${getPfScrollShopifySectionRootId(section) || "section"}-${
        getPfScrollProductId(section) || "product"
      }-${rowIndex}`
    );
    const elements = collectPfScrollCloneElements(root);
    const idMappings = new Map();
    const idOccurrences = new Map();

    elements.forEach((element) => {
      const sourceId = element.getAttribute("id")?.trim();
      if (!sourceId) return;
      const occurrence = (idOccurrences.get(sourceId) || 0) + 1;
      idOccurrences.set(sourceId, occurrence);
      const cloneId = `${sourceId}--${cloneNamespace}${occurrence > 1 ? `-${occurrence}` : ""}`;
      const mappings = idMappings.get(sourceId) || [];
      mappings.push({ element, cloneId });
      idMappings.set(sourceId, mappings);
      element.setAttribute("id", cloneId);
    });

    const targetedMediaTokens = new Set();
    elements.forEach((element) => {
      const target = element.getAttribute("data-target")?.trim();
      if (target && !target.startsWith("#")) targetedMediaTokens.add(target);
    });

    const mediaTokenMap = new Map();
    elements.forEach((element) => {
      const mediaId = element.getAttribute("data-media-id")?.trim();
      const isLocalDeferredMedia = element.matches?.("deferred-media, product-model");
      const isLocalMediaToken =
        mediaId &&
        (!/^\d+$/.test(mediaId) || targetedMediaTokens.has(mediaId) || isLocalDeferredMedia);
      if (!isLocalMediaToken || mediaTokenMap.has(mediaId)) return;
      mediaTokenMap.set(mediaId, `${mediaId}--${cloneNamespace}`);
    });

    const resolveMappedId = (sourceId, referenceElement) => {
      const mappings = idMappings.get(sourceId) || [];
      if (mappings.length <= 1) return mappings[0]?.cloneId || sourceId;

      const referenceRoot = referenceElement?.getRootNode?.();
      const sameRootMappings = mappings.filter(
        ({ element }) => element.getRootNode?.() === referenceRoot
      );
      if (sameRootMappings.length === 1) return sameRootMappings[0].cloneId;

      const referenceSvg = referenceElement?.closest?.("svg");
      if (referenceSvg) {
        const sameSvgMapping = sameRootMappings.find(
          ({ element }) => element.closest?.("svg") === referenceSvg
        );
        if (sameSvgMapping) return sameSvgMapping.cloneId;
      }

      let ancestor = referenceElement?.parentElement;
      while (ancestor) {
        let containedMapping = null;
        let containedMappingCount = 0;
        for (let mappingIndex = 0; mappingIndex < sameRootMappings.length; mappingIndex += 1) {
          const mapping = sameRootMappings[mappingIndex];
          if (ancestor.contains(mapping.element)) {
            containedMapping = mapping;
            containedMappingCount += 1;
          }
        }
        if (containedMappingCount === 1) return containedMapping.cloneId;
        ancestor = ancestor.parentElement;
      }

      return (sameRootMappings[0] || mappings[0]).cloneId;
    };
    const remapSingleId = (value, referenceElement) => resolveMappedId(value, referenceElement);
    const remapIdList = (value, referenceElement) =>
      value
        .split(/\s+/)
        .filter(Boolean)
        .map((sourceId) => remapSingleId(sourceId, referenceElement))
        .join(" ");
    const remapFragment = (value, referenceElement) => {
      if (!value?.startsWith("#")) return value;
      const sourceId = value.slice(1);
      if (!idMappings.has(sourceId)) return value;
      return `#${resolveMappedId(sourceId, referenceElement)}`;
    };
    const remapCssUrlFragments = (value, referenceElement) =>
      value.replace(/url\(\s*(["']?)#([^\s)"']+)\1\s*\)/g, (match, quote, sourceId) => {
        const cloneId = idMappings.has(sourceId) ? resolveMappedId(sourceId, referenceElement) : "";
        return cloneId ? `url(${quote}#${cloneId}${quote})` : match;
      });

    const singleIdRefAttributes = new Set([
      "for",
      "list",
      "form",
      "popovertarget",
      "commandfor",
      "aria-activedescendant",
      "aria-details",
      "aria-errormessage",
    ]);
    const idListAttributes = new Set([
      "aria-labelledby",
      "aria-describedby",
      "aria-controls",
      "aria-owns",
      "headers",
    ]);
    const fragmentAttributes = new Set([
      "href",
      "xlink:href",
      "data-modal",
      "data-target",
      "data-bs-target",
    ]);
    const localDataIdAttributes = new Set([
      "data-media-id",
      "data-deferred-media-id",
      "data-poster-id",
      "data-controls",
    ]);

    elements.forEach((element) => {
      Array.from(element.attributes).forEach((attribute) => {
        const { name } = attribute;
        let { value } = attribute;

        if (singleIdRefAttributes.has(name)) {
          value = remapSingleId(value, element);
        } else if (idListAttributes.has(name)) {
          value = remapIdList(value, element);
        } else if (fragmentAttributes.has(name)) {
          value = remapFragment(value, element);
          if (name === "data-target" && mediaTokenMap.has(value)) {
            value = mediaTokenMap.get(value);
          }
        } else if (localDataIdAttributes.has(name)) {
          const isExternalModalMediaId =
            name === "data-media-id" && element.matches?.(".product__media-toggle");
          if (!isExternalModalMediaId) {
            value =
              (idMappings.has(value) ? resolveMappedId(value, element) : "") ||
              mediaTokenMap.get(value) ||
              value;
          }
        }

        value = remapCssUrlFragments(value, element);
        if (value !== attribute.value) element.setAttribute(name, value);
      });
    });

    root.dataset.pfCloneNamespace = cloneNamespace;
  };

  const buildMediaItemsFromRows = (section, mediaPanel, rows) => {
    const priorSlots = Array.from(mediaPanel.querySelectorAll(selectors.mediaItem)).map(
      (mediaItem) => {
        const slot = mediaItem.querySelector(".pf-product-scroll__item-media");
        return slot ? slot.cloneNode(true) : null;
      }
    );

    const reviewStashKey = getPfScrollReviewStashKey(section);
    const reviewStash = loadPfScrollReviewStash(reviewStashKey);

    priorSlots.forEach((prior, i) => {
      if (
        prior &&
        itemMediaIsPfScrollReviewSlot(prior) &&
        isPfScrollLeftPanelMediaMeaningful(prior)
      ) {
        reviewStash[String(i)] = prior.outerHTML;
      }
    });

    rows.forEach((row, i) => {
      const im = row.querySelector(".pf-product-scroll__item-media");
      if (im && itemMediaIsPfScrollReviewSlot(im) && isPfScrollLeftPanelMediaMeaningful(im)) {
        reviewStash[String(i)] = im.outerHTML;
      }
    });

    mediaPanel.innerHTML = "";

    rows.forEach((row, index) => {
      const mediaItem = document.createElement("div");
      mediaItem.className = "pf-product-scroll__media-item";
      mediaItem.dataset.blockIndex = String(index);

      const itemMedia = row.querySelector(".pf-product-scroll__item-media");
      let slotToAppend = null;

      if (isPfScrollLeftPanelMediaMeaningful(itemMedia)) {
        slotToAppend = itemMedia.cloneNode(true);
      }

      if (
        !slotToAppend &&
        itemMediaIsPfScrollReviewSlot(itemMedia) &&
        !isPfScrollLeftPanelMediaMeaningful(itemMedia)
      ) {
        const stored = reviewStash[String(index)];
        const restored = clonePfScrollItemMediaFromStashedOuterHtml(stored);
        if (restored && isPfScrollLeftPanelMediaMeaningful(restored)) {
          slotToAppend = restored;
        }
      }

      if (slotToAppend) {
        stripLegacyPfScrollReviewMarkup(slotToAppend);
        stripPfThemeGalleryBindMarkers(slotToAppend);
        remapPfScrollCloneLocalReferences(slotToAppend, section, index);
        mediaItem.appendChild(slotToAppend);
        if (
          itemMediaIsPfScrollReviewSlot(slotToAppend) &&
          isPfScrollLeftPanelMediaMeaningful(slotToAppend)
        ) {
          reviewStash[String(index)] = slotToAppend.outerHTML;
        }
      } else {
        mediaItem.hidden = true;
      }

      mediaPanel.appendChild(mediaItem);
    });

    const hasMedia = Boolean(mediaPanel.querySelector(`${selectors.mediaItem}:not([hidden])`));
    mediaPanel.hidden = !hasMedia;
    section.classList.toggle("pf-product-scroll--no-media", !hasMedia);

    savePfScrollReviewStash(reviewStashKey, reviewStash);
  };

  const setActiveIndex = (mediaPanel, mediaItems, index) => {
    const normalizedIndex =
      Number.isInteger(index) && index >= 0 && index < mediaItems.length ? index : -1;

    mediaPanel.classList.toggle("has-active", normalizedIndex >= 0);
    mediaItems.forEach((item) => item.classList.remove("is-active"));

    if (normalizedIndex >= 0) {
      mediaItems[normalizedIndex].classList.add("is-active");
    }
  };

  const getNearestMeaningfulMediaIndex = (mediaItems, rowIndex) => {
    if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= mediaItems.length) return -1;

    for (let index = rowIndex; index >= 0; index -= 1) {
      if (!mediaItems[index].hidden) return index;
    }

    for (let index = rowIndex + 1; index < mediaItems.length; index += 1) {
      if (!mediaItems[index].hidden) return index;
    }

    return -1;
  };

  const syncVariantOptionBlocks = (section) => {
    const getOptionTargets = () => Array.from(section.querySelectorAll(selectors.variantOption));
    const optionTargets = getOptionTargets();
    debugPfScrollVariantSelectors("syncVariantOptionBlocks start", {
      sectionId: section?.id,
      optionTargetCount: optionTargets.length,
    });
    if (!optionTargets.length) return;

    const syncPfScrollCloneFormReferences = (root) => {
      const productInfo = getPfScrollProductInfo(section) || getMainProductInfo();
      const form = productInfo?.querySelector('form[id^="product-form-"]');
      const formId = form?.getAttribute("id")?.trim();
      if (!formId) return null;
      root.querySelectorAll("[form]").forEach((el) => {
        el.setAttribute("form", formId);
      });
      return { form, formId };
    };

    const syncAdaptiveConditionPosition = () => {
      const adaptiveRoot = section.querySelector("[data-pf-adaptive-options]");
      if (!adaptiveRoot) return;
      const conditionRow = getPfScrollOptionRows(section).find(
        (row) => !row.hidden && isPfScrollConditionOptionName(row.dataset.optionName)
      );
      if (conditionRow?.dataset?.optionPosition) {
        adaptiveRoot.dataset.pfConditionOptionPosition = conditionRow.dataset.optionPosition;
      } else {
        delete adaptiveRoot.dataset.pfConditionOptionPosition;
      }
    };

    const renderTarget = (target) => {
      const optionIndex = Number(
        target.dataset.optionPosition || target.dataset.variantOptionIndex
      );
      const hideOptionLabel = target.dataset.hideOptionLabel === "true";
      debugPfScrollVariantSelectors("render target start", {
        sectionId: section?.id,
        blockId: target.dataset.variantOptionBlock,
        optionIndex,
        hideOptionLabel,
        existingChildCount: target.children.length,
      });
      if (!optionIndex) {
        debugPfScrollVariantSelectors("render target cleared: missing option index", {
          blockId: target.dataset.variantOptionBlock,
        });
        return;
      }

      const sourceMatch = findPfScrollVariantSourceForTarget(section, target, optionIndex);
      const { fieldset: sourceFieldset, source: liveSource } = sourceMatch;
      if (!liveSource) {
        debugPfScrollVariantSelectors("render target preserved: no live variant source", {
          blockId: target.dataset.variantOptionBlock,
          optionIndex,
        });
        target.dataset.pfOptionControlsStatus = "stale";
        target.closest("[data-pf-option-row]")?.removeAttribute("hidden");
        return;
      }

      if (!sourceFieldset) {
        debugPfScrollVariantSelectors("render target preserved: no fieldset for option index", {
          blockId: target.dataset.variantOptionBlock,
          optionIndex,
          liveSourceId: liveSource.id,
          availableFieldsets: getProductInfoVariantSelectSources(section).reduce(
            (fieldsets, source) => [
              ...fieldsets,
              ...Array.from(source.querySelectorAll("fieldset.js.product-form__input")).map(
                (fieldset) =>
                  fieldset.dataset.optionIndex ||
                  fieldset.querySelector("legend")?.textContent?.trim()
              ),
            ],
            []
          ),
        });
        target.dataset.pfOptionControlsStatus = "stale";
        target.closest("[data-pf-option-row]")?.removeAttribute("hidden");
        return;
      }

      const optionRow = target.closest("[data-pf-option-row]");
      const sourceOptionName = getPfScrollFieldsetOptionName(sourceFieldset);
      const sourceOptionPosition = Number(sourceFieldset.dataset.optionIndex) || optionIndex;
      optionRow?.removeAttribute("hidden");
      if (sourceOptionName) {
        target.dataset.optionName = sourceOptionName;
        optionRow?.setAttribute("data-option-name", sourceOptionName);
      }
      target.dataset.optionPosition = String(sourceOptionPosition);
      target.dataset.variantOptionIndex = String(sourceOptionPosition);
      optionRow?.setAttribute("data-option-position", String(sourceOptionPosition));
      optionRow?.querySelectorAll("[data-pf-option-heading]").forEach((heading) => {
        // The guided test writes its own two-part heading ("Color. Pick the one you like.").
        if (heading.classList.contains("pf-guided-heading")) return;
        heading.textContent = `Choose Your ${sourceOptionName}`;
      });

      const clone = sourceFieldset.cloneNode(true);
      clone.dataset.optionName = sourceOptionName;
      clone.dataset.optionPosition = String(sourceOptionPosition);
      clone.dataset.optionIndex = String(sourceOptionPosition);
      stripSelectPrefixFromPfScrollVariantClone(clone);
      const blockId = target.dataset.variantOptionBlock || String(optionIndex);
      const idMap = new Map();

      clone.querySelectorAll("[id]").forEach((node) => {
        const newId = `${node.id}--${blockId}`;
        idMap.set(node.id, newId);
        node.id = newId;
      });

      clone.querySelectorAll("label[for]").forEach((label) => {
        const mapped = idMap.get(label.getAttribute("for"));
        if (mapped) {
          label.setAttribute("for", mapped);
        }
      });

      clone.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.name = `${input.name}--${blockId}`;

        input.addEventListener("change", () => {
          if (!input.checked) return;

          const liveMatch = findPfScrollVariantSourceForTarget(
            section,
            target,
            sourceOptionPosition
          );
          const { fieldset: liveFieldset, source: live } = liveMatch;
          if (!liveFieldset) {
            debugPfScrollVariantSelectors("change skipped: no live fieldset", {
              blockId,
              optionIndex,
              inputValue: input.value,
            });
            return;
          }

          const requestedProductPath = getPfScrollComparableProductUrl(input.dataset.productUrl);
          const currentProductPath = getPfScrollComparableProductUrl(
            getMainProductInfo()?.dataset.url || getPfScrollProductInfo(section)?.dataset.url
          );
          const isCrossProductSelection =
            Boolean(requestedProductPath) &&
            Boolean(currentProductPath) &&
            requestedProductPath !== currentProductPath;
          let dispatchFieldset = liveFieldset;
          let crossProductDispatchSource = live;

          if (isCrossProductSelection) {
            const pilotSource = getPfScrollProductInfo(section)?.querySelector(
              'variant-selects:not([data-upsell-isolated="true"])'
            );
            const pilotFieldset = findFieldsetForPfScrollTarget(
              pilotSource,
              target,
              sourceOptionPosition
            );
            if (!pilotSource || !pilotFieldset) {
              target.dataset.pfOptionControlsStatus = "stale";
              debugPfScrollVariantSelectors("cross-product change blocked: no pilot source", {
                blockId,
                optionIndex,
                inputValue: input.value,
                requestedProductPath,
              });
              return;
            }
            dispatchFieldset = pilotFieldset;
            crossProductDispatchSource = pilotSource;
          }

          const productInfo =
            crossProductDispatchSource.closest("pf-pilot-product-info") ||
            crossProductDispatchSource.closest("product-info") ||
            getMainProductInfo();
          const willRedirect = productInfo?.dataset.redirectOnVariantChange === "true";
          debugPfScrollVariantSelectors("change forwarding to live picker", {
            blockId,
            optionIndex,
            inputValue: input.value,
            liveSourceId: crossProductDispatchSource?.id,
            productInfoId: productInfo?.id,
            productInfoUpdateUrl: productInfo?.dataset.updateUrl,
            willRedirect,
            isCrossProductSelection,
          });

          stashRedirectScrollTarget(section, target, productInfo);
          const guidedStep = willRedirect
            ? null
            : window.PfScrollGuidedSteps?.onPick({ row: optionRow }) || null;
          stashPostAjaxVariantScrollTarget(section, blockId, willRedirect, guidedStep);

          const previousSelectedValue = getLiveCheckedRadioInFieldset(
            liveFieldset,
            sourceOptionPosition
          )?.value;
          applyPfScrollRadioSelection(dispatchFieldset, input);
          if (dispatchFieldset !== liveFieldset) {
            // Keep the hidden native picker visually in sync without letting it
            // fetch the sibling's assigned template. The pilot picker performs
            // the full-main swap through ?view=pf-scroll-pilot instead.
            applyPfScrollRadioSelection(liveFieldset, input);
          }

          const selectedValue = getLiveCheckedRadioInFieldset(
            dispatchFieldset,
            sourceOptionPosition
          );
          const selectedValueLabel = dispatchFieldset.querySelector(".selected-swatch-value");
          if (selectedValue && selectedValueLabel) {
            selectedValueLabel.textContent = selectedValue.value;
          }

          if (selectedValue) {
            stagePfScrollOptionAnalytics(section, {
              optionName: sourceOptionName,
              optionPosition: sourceOptionPosition,
              newValue: selectedValue.value,
              previousValue: previousSelectedValue,
            });
            selectedValue.dispatchEvent(new Event("change", { bubbles: true }));
          }

          const focusToken = stashPfScrollVariantFocus(section);
          getOptionTargets().forEach((optionTarget) => {
            renderTarget(optionTarget);
          });
          syncAdaptiveConditionPosition();
          restorePfScrollVariantFocus(section, focusToken);
        });
      });

      syncPfScrollRadioCloneCheckedFromLive(clone, sourceFieldset, sourceOptionPosition);

      if (hideOptionLabel) {
        clone.querySelectorAll(".product-form__form-header").forEach((header) => {
          const legend = header.querySelector("legend.form__label");
          if (legend) {
            legend.classList.add("visually-hidden");
            clone.insertBefore(legend, clone.firstChild);
          }
          header.remove();
        });
        clone
          .querySelectorAll("legend.form__label, label.form__label")
          .forEach((label) => label.classList.add("visually-hidden"));
      }

      const previousControls = Array.from(target.children).filter((node) =>
        node.hasAttribute("data-option-index")
      );
      const formOwner = syncPfScrollCloneFormReferences(clone);
      target.appendChild(clone);
      const cloneInputs = Array.from(clone.querySelectorAll('input[type="radio"]'));
      const hasValidFormOwnership =
        Boolean(formOwner) &&
        cloneInputs.length > 0 &&
        cloneInputs.every(
          (input) =>
            input.getAttribute("form") === formOwner.formId && input.form === formOwner.form
        );
      if (!hasValidFormOwnership) {
        clone.remove();
        target.dataset.pfOptionControlsStatus = "stale";
        target.closest("[data-pf-option-row]")?.removeAttribute("hidden");
        return;
      }
      previousControls.forEach((node) => node.remove());
      delete target.dataset.pfOptionControlsStatus;
      debugPfScrollVariantSelectors("render target appended clone", {
        blockId,
        optionIndex,
        cloneInputCount: clone.querySelectorAll('input[type="radio"]').length,
        targetChildCount: target.children.length,
      });
    };

    function refreshVariantOptionClones() {
      const focusToken = stashPfScrollVariantFocus(section);
      getOptionTargets().forEach((target) => {
        renderTarget(target);
      });
      syncAdaptiveConditionPosition();
      restorePfScrollVariantFocus(section, focusToken);
    }

    refreshVariantOptionClones();
    variantSectionRefreshers.set(section, refreshVariantOptionClones);
  };

  function initProductScroll(section, lifecycleSignal) {
    debugPfScrollVariantSelectors("initProductScroll start", {
      shopifySectionId: section?.closest?.('[id^="shopify-section-"]')?.id,
      variantTargetCount: section?.querySelectorAll?.(selectors.variantOption).length,
      hasScrollProductInfo: Boolean(getPfScrollProductInfo(section)),
    });

    if (
      schedulePfScrollProductContextRebuild(
        section,
        getPfScrollSelectedVariant(getPfScrollProductInfo(section))
      )
    ) {
      return;
    }

    initPfCtaVideoDrawers(section);
    initPfScrollComplementarySelectors(section, lifecycleSignal);
    initPfScrollWarrantySelectors(section, lifecycleSignal);

    let headerTicking = false;
    const updateHeaderOffset = () => {
      setSectionHeaderOffset(section);
    };
    const queueHeaderOffset = () => {
      if (headerTicking) return;
      headerTicking = true;
      window.requestAnimationFrame(() => {
        updateHeaderOffset();
        headerTicking = false;
      });
    };

    updateHeaderOffset();

    syncPfScrollProductInfoState(section);
    syncVariantOptionBlocks(section);

    const mediaPanel = section.querySelector(selectors.mediaPanel);
    const rows = Array.from(section.querySelectorAll(selectors.row));
    const contentBoxes = rows.map((row) => row.querySelector(selectors.contentBox)).filter(Boolean);

    syncPfScrollItemMediaLayoutClasses(section);

    if (!mediaPanel || !rows.length || !contentBoxes.length) {
      initPfThemeGallerySegmentBars(section);
      return;
    }

    buildMediaItemsFromRows(section, mediaPanel, rows);
    syncPfScrollItemMediaLayoutClasses(section);
    syncPfScrollComplementaryRowVisibility(section);
    syncPfPilotBoostOfferRowVisibility(section);
    syncPfScrollWarrantySelector(section);
    syncPfScrollGalleryMainModals();
    initPfThemeGallerySegmentBars(section);
    const mediaItems = Array.from(section.querySelectorAll(selectors.mediaItem));

    if (!mediaItems.length) {
      initPfThemeGallerySegmentBars(section);
      return;
    }

    let isTicking = false;

    const updateActiveFromViewport = () => {
      const viewportCenterY = window.innerHeight * swapTriggerRatio;
      const sectionRect = section.getBoundingClientRect();

      if (sectionRect.top > window.innerHeight || sectionRect.bottom < 0) {
        setActiveIndex(mediaPanel, mediaItems, -1);
        return;
      }

      let activeRowIndex = mediaItems.findIndex((item) => !item.hidden);
      if (activeRowIndex === -1) {
        setActiveIndex(mediaPanel, mediaItems, -1);
        return;
      }

      contentBoxes.forEach((box, i) => {
        if (box.closest(selectors.row)?.hidden) return;
        const rect = box.getBoundingClientRect();
        if (rect.top <= viewportCenterY) {
          activeRowIndex = i;
        }
      });

      setActiveIndex(
        mediaPanel,
        mediaItems,
        getNearestMeaningfulMediaIndex(mediaItems, activeRowIndex)
      );
    };

    const queueUpdate = () => {
      if (isTicking) return;
      isTicking = true;

      window.requestAnimationFrame(() => {
        updateActiveFromViewport();
        isTicking = false;
      });
    };

    const enableDesktopBehavior = () => {
      setActiveIndex(
        mediaPanel,
        mediaItems,
        mediaItems.findIndex((item) => !item.hidden)
      );
      updateActiveFromViewport();
      window.addEventListener("scroll", queueUpdate, { passive: true, signal: lifecycleSignal });
      window.addEventListener("resize", queueUpdate, { signal: lifecycleSignal });
    };

    const disableDesktopBehavior = () => {
      window.removeEventListener("scroll", queueUpdate, { passive: true });
      window.removeEventListener("resize", queueUpdate);
      mediaItems.forEach((item) => item.classList.remove("is-active"));
    };

    const handleViewportChange = (event) => {
      updateHeaderOffset();
      if (event.matches) {
        enableDesktopBehavior();
        return;
      }
      disableDesktopBehavior();
    };

    handleViewportChange(desktopMediaQuery);
    desktopMediaQuery.addEventListener("change", handleViewportChange, { signal: lifecycleSignal });
    window.addEventListener("resize", queueHeaderOffset, { signal: lifecycleSignal });
    window.addEventListener("scroll", queueHeaderOffset, {
      passive: true,
      signal: lifecycleSignal,
    });

    let headerObserver;
    if (window.ResizeObserver) {
      headerObserver = new ResizeObserver(() => {
        queueHeaderOffset();
      });
      getHeaderWatchTargets().forEach((target) => headerObserver.observe(target));
    }

    lifecycleSignal.addEventListener(
      "abort",
      () => {
        disableDesktopBehavior();
        if (headerObserver) headerObserver.disconnect();
      },
      { once: true }
    );
  }

  let pfProductGalleryDelegationBound = false;
  let pfScrollDescriptionDelegationBound = false;
  let pfScrollDescriptionResizeBound = false;

  const getPfScrollDescriptionTwoLineHeight = (textElement) => {
    const styles = window.getComputedStyle(textElement);
    const lineHeight = Number.parseFloat(styles.lineHeight);
    const fontSize = Number.parseFloat(styles.fontSize);
    return (Number.isFinite(lineHeight) ? lineHeight : fontSize * 1.5) * 2 + 1;
  };

  const getPfScrollDescriptionButtonLabel = (button, labelType) => {
    const fallback = labelType === "readLess" ? "See Less" : "Read More";
    return (
      button?.dataset?.[
        `pfScrollDescription${labelType === "readLess" ? "ReadLess" : "ReadMore"}`
      ] || fallback
    );
  };

  const clampPfScrollDescription = (description, { resetExpanded = false } = {}) => {
    if (!description) return;
    if (resetExpanded) description.classList.remove("is-expanded");
    if (description.classList.contains("is-expanded")) return;
    const textElement = description.querySelector("[data-pf-scroll-description-text]");
    const button = description.querySelector("[data-pf-scroll-description-expand]");
    const fullText = description.dataset.pfScrollDescriptionFull || "";
    if (!textElement || !button || !fullText) return;

    textElement.textContent = fullText;
    button.textContent = getPfScrollDescriptionButtonLabel(button, "readMore");
    button.hidden = true;

    window.requestAnimationFrame(() => {
      const maxHeight = getPfScrollDescriptionTwoLineHeight(
        description.querySelector(".pf-scroll-product-description__text")
      );
      if (description.scrollHeight <= maxHeight) return;

      button.hidden = false;
      let low = 0;
      let high = fullText.length;
      let best = "";

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        textElement.textContent = `${fullText.slice(0, mid).trimEnd()}... `;

        if (description.scrollHeight <= maxHeight) {
          best = textElement.textContent;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      textElement.textContent = best || "... ";
    });
  };

  const clampPfScrollDescriptions = (root = document, options = {}) => {
    root.querySelectorAll("[data-pf-scroll-description]").forEach((description) => {
      clampPfScrollDescription(description, options);
    });
  };

  const setPfProductGalleryIndex = (root, nextIndex) => {
    const track = root.querySelector("[data-pf-gallery-track]");
    const slides = root.querySelectorAll("[data-pf-gallery-slide]");
    const dots = root.querySelectorAll("[data-pf-gallery-dot]");
    const count = slides.length;
    if (!track || count === 0) return;

    const i = ((nextIndex % count) + count) % count;
    root.dataset.galleryIndex = String(i);
    track.style.transform = `translate3d(-${i * 100}%, 0, 0)`;

    slides.forEach((slide, idx) => {
      slide.classList.toggle("is-active", idx === i);
      slide.setAttribute("aria-hidden", idx === i ? "false" : "true");
    });

    dots.forEach((dot, idx) => {
      dot.classList.toggle("is-active", idx === i);
      dot.setAttribute("aria-selected", idx === i ? "true" : "false");
    });
  };

  const bindPfProductGalleryClickDelegation = () => {
    if (pfProductGalleryDelegationBound) return;
    pfProductGalleryDelegationBound = true;

    document.addEventListener("click", (event) => {
      const prevBtn = event.target.closest("[data-pf-gallery-prev]");
      const nextBtn = event.target.closest("[data-pf-gallery-next]");
      const dotBtn = event.target.closest("[data-pf-gallery-dot]");
      if (!prevBtn && !nextBtn && !dotBtn) return;

      const galleryRoot = event.target.closest("[data-pf-product-gallery]");
      if (!galleryRoot) return;

      const slides = galleryRoot.querySelectorAll("[data-pf-gallery-slide]");
      const count = slides.length;
      if (count === 0) return;

      let nextIndex = Number.parseInt(galleryRoot.dataset.galleryIndex || "0", 10);
      if (Number.isNaN(nextIndex)) nextIndex = 0;

      if (prevBtn) nextIndex -= 1;
      else if (nextBtn) nextIndex += 1;
      else if (dotBtn)
        nextIndex = Number.parseInt(dotBtn.dataset.pfGalleryDotIndex || "0", 10) || 0;

      setPfProductGalleryIndex(galleryRoot, nextIndex);
    });
  };

  const bindPfScrollDescriptionDelegation = () => {
    if (pfScrollDescriptionDelegationBound) return;
    pfScrollDescriptionDelegationBound = true;

    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-pf-scroll-description-expand]");
      if (!button) return;
      const description = button.closest(".pf-scroll-product-description");
      if (!description) return;
      const textElement = description.querySelector("[data-pf-scroll-description-text]");
      if (description.classList.contains("is-expanded")) {
        clampPfScrollDescription(description, { resetExpanded: true });
        return;
      }

      description.classList.add("is-expanded");
      button.textContent = getPfScrollDescriptionButtonLabel(button, "readLess");
      if (textElement)
        textElement.textContent =
          description.dataset.pfScrollDescriptionFull || textElement.textContent;
    });

    if (!pfScrollDescriptionResizeBound) {
      pfScrollDescriptionResizeBound = true;
      window.addEventListener("resize", () => {
        clampPfScrollDescriptions();
      });
    }
  };

  const bootPfProductScrollSections = (root = document) => {
    applyMainVariantVisibilityFromDom();
    bindProductInfoVariantIntegrationOnce();
    bindPfScrollCartCoverageOnce();
    bindPfChoiceHelperCartTrackingOnce();

    scrollSectionLifecycleAbort.abort();
    scrollSectionLifecycleAbort = new AbortController();
    const lifecycleSignal = scrollSectionLifecycleAbort.signal;

    const sections = getPfScrollSectionsForInit(root);
    debugPfScrollVariantSelectors("bootPfProductScrollSections", {
      rootTag: root?.tagName,
      sectionCount: sections.length,
      designMode: isPfScrollDesignMode(),
    });

    sections.forEach((section) => {
      initProductScroll(section, lifecycleSignal);
    });

    syncPfScrollGalleryMainModals();
    initPfThemeGallerySegmentBars(root);
    initPfCtaVideoDrawers(root);
    clampPfScrollDescriptions(root, { resetExpanded: true });
    refreshPfScrollWishlistButtons(root);
  };

  // Boot as soon as the page is parsed. This script is deferred, so it runs after parsing, but
  // DOMContentLoaded also waits for every deferred script after it. The SureBright SDK loads last
  // and held DOMContentLoaded ~33s past parsing on a slow 4G phone, and the option rows have no
  // choices to tap until this runs.
  const bootPfProductScrollBehavior = () => {
    if (!document.querySelector(selectors.section)) return;
    if (window.pfPilotProductScrollBehaviorInitialized) {
      debugPfScrollVariantSelectors("duplicate initialization skipped");
      return;
    }
    window.pfPilotProductScrollBehaviorInitialized = true;

    bindPfProductGalleryClickDelegation();
    bindPfScrollDescriptionDelegation();
    bootPfProductScrollSections(document);
    restoreRedirectScrollTarget();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootPfProductScrollBehavior);
  } else {
    bootPfProductScrollBehavior();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!isPfProductScrollSectionRoot(event.target)) return;
    bootPfProductScrollSections(document);
  });

  document.addEventListener("shopify:section:select", (event) => {
    if (!isPfProductScrollSectionRoot(event.target)) return;
    window.requestAnimationFrame(() => {
      bootPfProductScrollSections(document);
    });
  });

  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target?.closest?.(selectors.section);
    if (!section) return;

    window.requestAnimationFrame(() => {
      const blockEl =
        event.target?.closest?.("[data-variant-option-block]") ||
        event.target?.closest?.(".pf-product-scroll__row") ||
        event.target;

      blockEl?.scrollIntoView?.({ block: "center", behavior: "auto" });
      bootPfProductScrollSections(section);
    });
  });
})();
